import { Alert, Button, TextField } from '@territory/ui';
import { BRAND } from './wedgeBrand';
import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { API, apiUrl } from '@territory/client/routes';
import { PinLogin } from './PinLogin';
import { detectInAppBrowser, tryOpenInSystemBrowser } from './wedgeInAppBrowser';
import { wedgeTrack } from './wedgeAnalytics';
import { setWedgeTokens } from '@territory/client/api';
import { isNativeIOS, signInWithApple, signInWithGoogle, googleNativeAvailable, signInWithMicrosoft, microsoftNativeAvailable } from './native/nativeBridge';

/**
 * Pre-auth landing for wedge.repvantage.io · the universal-access two-path
 * door (Luke directive: simple for absolutely everyone, no blockers beyond
 * basic security and infosec, plus 10 quality-of-life upgrades on top).
 *
 * 2026-06-02 (round 18) · upgrades layered on top of round 17:
 *
 *   M  · Internal @repvantage.io magic-link now works · the silent no-op
 *        is gone. Workspace SSO is still recommended but no longer required;
 *        an internal user on a fresh browser / personal device can use the
 *        same fallback as everyone else. Backend auto-activates internal
 *        PENDING_APPROVAL users on OAuth too (admin elevation still flows
 *        separately through AUTO_ADMIN_EMAILS).
 *   U1 · Remember last-used email · localStorage rv_wedge_last_email is read
 *        on mount and auto-fills the input so returning users have one less
 *        keystroke. Cleared by "Send to a different email" in the success
 *        state. Privacy: email never leaves the device until the user submits.
 *   U2 · Email domain typeahead · once the user types `@<partial>`, a small
 *        suggestion strip offers gmail.com / outlook.com / yahoo.com /
 *        icloud.com / hotmail.com (and the corp domains the rep has
 *        previously used). Tab / Enter accepts. Saves typing on mobile.
 *   U3 · Resend cooldown · "Check your inbox" state shows a 30-second
 *        countdown. After it elapses, "Resend" reactivates so a rep whose
 *        inbox is slow or who lost the link can retrigger without leaving
 *        the screen. Rate-limited at the backend too.
 *   U4 · Loading states · Google button transitions to "Redirecting..."
 *        with a spinner while the OAuth redirect fires (no double-click
 *        confusion). Magic-link button transitions to "Sending..." while
 *        the API call is in flight.
 *   U5 · Inline validation feedback · invalid email shows a soft amber
 *        message ONLY after the user blurs the field or clicks Send (never
 *        on every keystroke). Valid email shows a quiet green check.
 *   U6 · ?return= URL preserved · if the rep deep-linked to a specific
 *        wedge URL (e.g. /?account=hayesbeer&q=...) and was bounced to
 *        login, the magic JWT carries the original path. After redeem the
 *        MagicRedeem upstream component navigates back. Same-origin paths
 *        only; the backend rejects anything else as defense-in-depth.
 *   U7 · /api/auth/providers · the backend now publishes an enabled-
 *        provider list so the UI never shows a button that hits a 400.
 *        Today only Google is configured; the Microsoft button code is
 *        already wired and lights up the moment MICROSOFT_CLIENT_ID is set.
 *   U8 · Microsoft OAuth button · rendered alongside Google when the
 *        provider list includes 'microsoft'. Same auto-activate semantics.
 *        Built so Microsoft 365 customers have a one-tap path too.
 *   U9 · ?email= + ?autosend=1 · existing ?email= still pre-fills. Add
 *        ?autosend=1 (or ?send=1) and the magic-link send fires the moment
 *        the page mounts. Lets us share one-tap invite links that skip
 *        the typing step entirely.
 *   U10· Friendly error state · if BOTH SMTP and Resend fail, the magic-
 *        link branch surfaces "Email is having trouble · try Continue
 *        with Google instead" with a one-tap Google fallback button
 *        instead of a generic "Something went wrong" toast.
 *
 * Round-17 dead-simple two-path treatment (Continue with Google primary,
 * Email me a sign-in link inline secondary) is preserved. The 10 upgrades
 * sit underneath without adding any new top-level surfaces · the screen
 * still reads as "click one of two buttons" at first glance.
 *
 * Deep-link contract preserved exactly:
 *   - ?magic=<jwt>           → MagicRedeem (upstream)
 *   - ?code=XXX + ?email=    → CodeAutoRedeem (upstream)
 *   - ?error=<reason>        → AccessDenied (upstream)
 *   - ?email=<addr>          → pre-fill input
 *   - ?email=<addr>&autosend=1 → pre-fill + fire send on mount
 *   - ?return=<path>         → carried through magic JWT → redeem returns
 *
 * Legacy props (initialView, initialCode) are accepted but ignored ·
 * WedgeAuthGate still passes them and we keep the shape stable.
 */
const LAST_EMAIL_KEY = 'rv_wedge_last_email';
const KNOWN_DOMAINS_KEY = 'rv_wedge_known_domains';
const LOGIN_ATTEMPTS_KEY = 'rv_wedge_login_attempts';
/* 2026-06-02 · round 22 · I7 · Google attempt timestamps for retry-only
   prompt=consent. Rolling window of the last 60 seconds. */
const GOOGLE_ATTEMPTS_KEY = 'rv_wedge_google_attempts';
/* 2026-06-02 · round 20 · W10 · which path the user successfully used
   last time. Lets us surface that method first on return. Values:
   'google' | 'microsoft' | 'magic'. */
const LAST_METHOD_KEY = 'rv_wedge_last_method';
/* 2026-08-26 · Bug 9A · SIWA orphan 401 recovery (native iOS only). When a
   rep deletes their account and re-onboards, the on-device Sign in with Apple
   grant persists · Apple reuses it and returns NO email, so the backend 401s
   with code apple_email_hidden. The backend behavior is correct; the recovery
   is a client concern. We remember the orphan in sessionStorage (one-shot,
   cleared on the next successful sign-in) so the sign-in screen leads with the
   alternate paths that actually get the rep in on attempt 1 instead of
   re-presenting the same failing Apple button. */
const APPLE_GRANT_ORPHAN_KEY = 'rv_apple_grant_orphan';
const APPLE_EMAIL_HIDDEN_MSG =
  'Apple is not sharing your email with RepVantage. Open Settings, tap your name, tap Sign in with Apple, choose RepVantage, tap Stop Using Sign in with Apple, then sign in again.';
/* 2026-06-02 · round 20 · W1 · typo correction map. Maps common
   misspellings to the intended domain. Suggested via a small
   "Did you mean ...?" affordance under the input. Not auto-corrected ·
   the rep approves with one tap so we never silently change input. */
const DOMAIN_TYPOS: Record<string, string> = {
  'gmial.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmail.con': 'gmail.com',
  'outloo.com': 'outlook.com',
  'outlok.com': 'outlook.com',
  'outloook.com': 'outlook.com',
  'outlook.co': 'outlook.com',
  'hotmial.com': 'hotmail.com',
  'hotmai.com': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'yaho.com': 'yahoo.com',
  'yhaoo.com': 'yahoo.com',
  'yahoo.co': 'yahoo.com',
  'icould.com': 'icloud.com',
  'icoud.com': 'icloud.com',
  'iclou.com': 'icloud.com',
};
const RESEND_COOLDOWN_SECONDS = 30;
const LOOP_GUARD_WINDOW_MS = 10 * 60 * 1000;
const LOOP_GUARD_THRESHOLD = 3;
const DEFAULT_DOMAINS = [
  'gmail.com',
  'outlook.com',
  'yahoo.com',
  'icloud.com',
  'hotmail.com',
];
/* 2026-06-02 · round 19 · V6 · legacy localStorage keys that the dead
   persona-routed flow wrote. Cleaned on mount so the browser is not
   carrying half-state from the old PIN/code/persona routes. We never
   wipe a key the live flow actively uses (LAST_EMAIL_KEY,
   KNOWN_DOMAINS_KEY, LOGIN_ATTEMPTS_KEY). */
const LEGACY_PREFIXES_TO_PURGE = [
  'rv_wedge_pin_',
  'rv_wedge_code_',
  'rv_wedge_persona_',
  'rv_wedge_share_confirmed',
  'rv_wedge_yc_',
  'rv_wedge_walkin_',
];

/* 2026-06-02 · round 19 · V4 · mail-app deep links. Each entry is
   { test: domain matcher, label, url }. test=null is the always-shown
   default. The most relevant one for the rep's email domain renders
   first so iOS Gmail / Outlook users see their app at the top. */
type MailAppDeepLink = { label: string; url: string; matchDomains?: string[] };
const MAIL_APPS: MailAppDeepLink[] = [
  { label: 'Open Gmail', url: 'https://mail.google.com', matchDomains: ['gmail.com', 'googlemail.com'] },
  { label: 'Open Outlook', url: 'https://outlook.live.com/mail/0/inbox', matchDomains: ['outlook.com', 'hotmail.com', 'live.com', 'msn.com'] },
  { label: 'Open Yahoo', url: 'https://mail.yahoo.com', matchDomains: ['yahoo.com', 'ymail.com'] },
  { label: 'Open iCloud Mail', url: 'https://www.icloud.com/mail', matchDomains: ['icloud.com', 'me.com', 'mac.com'] },
];

type AuthProvider = 'google' | 'microsoft' | 'apple';

export function WedgeLanding({
  initialEmail = '',
  initialError = '',
}: {
  initialView?: string;
  initialCode?: string;
  initialEmail?: string;
  initialError?: string;
} = {}) {
  /* 2026-06-02 · round 19 · V6 · purge legacy persona-flow localStorage on
     mount. Runs once · idempotent. Never touches the keys the live flow
     uses (LAST_EMAIL_KEY, KNOWN_DOMAINS_KEY, LOGIN_ATTEMPTS_KEY). */
  useEffect(() => {
    try {
      if (typeof localStorage === 'undefined') return;
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;
        if (LEGACY_PREFIXES_TO_PURGE.some((prefix) => key === prefix || key.startsWith(prefix))) {
          toRemove.push(key);
        }
      }
      for (const k of toRemove) {
        try { localStorage.removeItem(k); } catch { /* skip individual key */ }
      }
    } catch { /* storage blocked · skip silently */ }
  }, []);

  /* U1 · last-used email seeds the input. ?email= URL param wins when
     present so a one-tap invite link still pre-fills the right address. */
  const seededEmail = useMemo(() => {
    if (initialEmail) return initialEmail;
    try {
      if (typeof localStorage === 'undefined') return '';
      return localStorage.getItem(LAST_EMAIL_KEY) || '';
    } catch {
      return '';
    }
  }, [initialEmail]);

  const [email, setEmail] = useState(seededEmail);
  const [emailTouched, setEmailTouched] = useState(false);
  /* 2026-06-03 · pin-login view. Wires PinLogin.tsx (was dead code) into
     the landing so manual user/password accounts (bcrypt-PIN in AIArtifact
     type='wedge_pin') have a reachable UI. Default view stays 'main' so
     the existing two-path (Google + magic-link) treatment is unchanged.
     The "Have a password? Sign in" entry sits BELOW the magic-link hint,
     never burying any persona route.

     2026-07-27 · that exact button is now web-only. The iOS shell renders
     its own subordinate pair under Sign in with Apple ("Already have an
     account? Sign in with your PIN" + "Lost your PIN? Email me a sign-in
     link") because the shell has no Google, magic-link or YC entry to sit
     beside. Both land on the same PinLogin / sign-in-link machinery the web
     uses. The PIN feature is untouched on both surfaces. */
  /* 2026-09-27 · 'yc' view dropped: nothing on the landing ever set it after
     the 2026-07-31 button removal. The ?yc= deep link still redeems upstream
     in WedgeAuthGate / CodeAutoRedeem. */
  const [view, setView] = useState<'main' | 'pin' | 'recover'>('main');
  /* 2026-08-19 · email + password sign-in state. 2026-09-27 · the form it
     drives renders on the native iOS shell only; web uses Google plus the
     one-tap sign-in link (magicState below). Backend routes stay live. */
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [pwName, setPwName] = useState('');
  const [pwState, setPwState] = useState<'idle' | 'submitting'>('idle');
  const [pwError, setPwError] = useState('');
  const [pwNotice, setPwNotice] = useState('');
  const [forgotState, setForgotState] = useState<'idle' | 'sending' | 'sent'>('idle');
  /* 2026-07-27 · native-only "Lost your PIN?" recovery. Reuses the existing
     POST /api/auth/wedge-access/sign-in-link endpoint (no new route). The
     confirmation is deliberately neutral · the backend always returns ok so
     an attacker cannot enumerate registered emails, and the UI must not leak
     what the backend refuses to. */
  const [recoverEmail, setRecoverEmail] = useState('');
  const [recoverState, setRecoverState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [recoverError, setRecoverError] = useState('');
  const [magicState, setMagicState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [magicError, setMagicError] = useState<string>('');
  const [magicErrorBoth, setMagicErrorBoth] = useState(false);
  const [googleState, setGoogleState] = useState<'idle' | 'redirecting'>('idle');
  const [msState, setMsState] = useState<'idle' | 'redirecting'>('idle');
  const [appleState, setAppleState] = useState<'idle' | 'redirecting' | 'authorizing'>('idle');
  /* iOS native shell (App Review 4.8) · isNativeIOS() is false in every
     browser, so the native Apple button below never renders on
     wedge.repvantage.io and the web landing is untouched. */
  const nativeIOS = useMemo(() => {
    try { return isNativeIOS(); } catch { return false; }
  }, []);
  const [appleError, setAppleError] = useState<string>('');
  /* 2026-08-26 · Bug 9A · seeds true when the on-device Apple grant is a known
     orphan (set on a prior apple_email_hidden 401 this session). Reactive so
     the render leads with the alternate-sign-in affordance. Reading
     sessionStorage on web is inert · the flag is only ever written on the
     native shell, so the web path stays behavior-identical. */
  const [appleGrantOrphan, setAppleGrantOrphan] = useState<boolean>(() => {
    try {
      return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(APPLE_GRANT_ORPHAN_KEY) === '1';
    } catch {
      return false;
    }
  });
  const clearAppleGrantOrphan = () => {
    try {
      if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(APPLE_GRANT_ORPHAN_KEY);
    } catch { /* storage blocked · skip */ }
    setAppleGrantOrphan(false);
  };
  /* 2026-08-04 (founder) · native Google sign-in. googleNativeReady stays false
     on web and on any iOS build without the GoogleAuth plugin, so the native
     Google button self-hides until the plugin + iOS OAuth client are really in
     the build · never a dead control. */
  const [googleNativeReady, setGoogleNativeReady] = useState(false);
  const [googleNativeState, setGoogleNativeState] = useState<'idle' | 'authorizing'>('idle');
  const [googleNativeError, setGoogleNativeError] = useState<string>('');
  useEffect(() => {
    let alive = true;
    void googleNativeAvailable().then((ok) => { if (alive) setGoogleNativeReady(ok); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  /* 2026-08-20 (founder · MS-on-iOS G1) · native Microsoft sign-in.
     microsoftNativeReady stays false on web and on any iOS build without the
     Browser plugin (the installed-app PKCE flow opens it), so the native
     Microsoft button self-hides until it can really run · never a dead control.
     Mirrors the native Google probe above. startMicrosoftSignIn already
     auto-routes to the native PKCE branch, so no separate handler is needed. */
  const [microsoftNativeReady, setMicrosoftNativeReady] = useState(false);
  useEffect(() => {
    let alive = true;
    void microsoftNativeAvailable().then((ok) => { if (alive) setMicrosoftNativeReady(ok); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const [topError, setTopError] = useState<string>(initialError || '');
  useEffect(() => { if (initialError) setTopError(initialError); }, [initialError]);

  /* 2026-06-02 · round 19 · V3 · aria-live region for state announcements.
     When magicState transitions, screen readers announce the result.
     "polite" means it waits for the current speech to finish · never
     interrupts the rep mid-action. */
  const liveMessage = (() => {
    if (magicState === 'sending') return 'Sending a sign-in link to your email.';
    if (magicState === 'sent') return `A sign-in link is on its way to ${email}. Check your inbox.`;
    if (magicState === 'error' && magicErrorBoth) return 'Email delivery is having trouble. Use Continue with Google.';
    if (magicState === 'error' && magicError) return magicError;
    return '';
  })();

  /* 2026-06-02 · round 19 · V2 · auto-focus the email input on first
     paint when there is no seeded value · saves a tap on mobile. We skip
     auto-focus when seededEmail is set (returning user) so the iOS
     keyboard does not pop up unprompted for someone who just wants to
     click Continue with Google. */
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (seededEmail) return;
    /* Defer a tick so the input renders before we focus · iOS Safari
       sometimes drops focus calls that race the render. */
    const t = setTimeout(() => {
      try { emailInputRef.current?.focus({ preventScroll: true }); } catch { /* no-op */ }
    }, 80);
    return () => clearTimeout(t);
  }, [seededEmail]);

  /* 2026-06-02 · round 19 · V7 · login-loop guard. We track magic-link
     send timestamps in localStorage and surface an explicit "try Google
     instead" banner after THRESHOLD attempts within WINDOW_MS · so a
     rep who can't see their email is steered to the path that will
     definitely work for them rather than spinning. */
  const [loopBannerVisible, setLoopBannerVisible] = useState(false);
  const checkLoopGuard = () => {
    try {
      if (typeof localStorage === 'undefined') return;
      const now = Date.now();
      const raw = localStorage.getItem(LOGIN_ATTEMPTS_KEY);
      const list = raw ? (JSON.parse(raw) as unknown) : [];
      const arr = Array.isArray(list)
        ? list.filter((x): x is number => typeof x === 'number' && now - x < LOOP_GUARD_WINDOW_MS)
        : [];
      if (arr.length >= LOOP_GUARD_THRESHOLD) setLoopBannerVisible(true);
    } catch { /* storage blocked · skip */ }
  };
  useEffect(() => { checkLoopGuard(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const recordLoginAttempt = () => {
    try {
      if (typeof localStorage === 'undefined') return;
      const now = Date.now();
      const raw = localStorage.getItem(LOGIN_ATTEMPTS_KEY);
      const list = raw ? (JSON.parse(raw) as unknown) : [];
      const arr = Array.isArray(list)
        ? list.filter((x): x is number => typeof x === 'number' && now - x < LOOP_GUARD_WINDOW_MS)
        : [];
      const next = [now, ...arr].slice(0, 10);
      localStorage.setItem(LOGIN_ATTEMPTS_KEY, JSON.stringify(next));
      if (next.length >= LOOP_GUARD_THRESHOLD) setLoopBannerVisible(true);
    } catch { /* storage blocked · skip */ }
  };

  /* U7 · fetch the enabled provider list on mount and gate the OAuth
     buttons by it. While the request is in flight we render Google
     optimistically (the legacy + safe default) and add others as they
     arrive. Failure is silent · Google stays visible regardless. */
  const [providers, setProviders] = useState<AuthProvider[]>(['google']);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(apiUrl('/api/auth/providers'), { credentials: 'include' });
        if (!res.ok) return;
        const data = (await res.json()) as { providers?: string[] };
        if (!alive || !Array.isArray(data.providers)) return;
        const next = data.providers
          .filter((p): p is AuthProvider => p === 'google' || p === 'microsoft' || p === 'apple');
        if (next.length) setProviders(next);
      } catch {
        /* network blip · stick with the optimistic default */
      }
    })();
    return () => { alive = false; };
  }, []);

  /* U6 · capture the optional ?return= deep-link path at mount so the
     magic-link send can embed it. The backend validates same-origin so
     anything malformed is rejected at mint time.

     2026-06-02 · round 20 · W3 · also capture window.location.hash so
     deep-links like /?account=ABC#tab=signals come back intact. The
     hash is appended to the returnTo · backend regex `/^\/[^/]/`
     still matches because the first non-/ character is `?`. */
  const returnTo = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = params.get('return') || '';
      const trimmed = raw.trim().slice(0, 400);
      const validPath = /^\/[^/]/.test(trimmed) || trimmed === '/' ? trimmed : '';
      if (!validPath) return '';
      const hash = (window.location.hash || '').slice(0, 100);
      return hash ? `${validPath}${hash}`.slice(0, 400) : validPath;
    } catch {
      return '';
    }
  }, []);

  /* 2026-06-02 · round 20 · W4 · offline state. Surfaces "you appear to
     be offline" above the form when navigator.onLine flips false, and
     auto-retries the most recent magic-link send when online comes back. */
  const [isOnline, setIsOnline] = useState<boolean>(() => {
    try { return typeof navigator !== 'undefined' ? navigator.onLine : true; } catch { return true; }
  });
  const lastAttemptedEmail = useRef<string>('');
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onOnline = () => {
      setIsOnline(true);
      /* If we failed mid-send while offline, automatically retry · the
         rep doesn't have to remember to tap Resend again. */
      if (magicState === 'error' && lastAttemptedEmail.current) {
        void sendMagicLink(lastAttemptedEmail.current);
      }
    };
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [magicState]); // eslint-disable-line react-hooks/exhaustive-deps

  /* 2026-06-02 · round 20 · W8 · third-party cookie / storage block
     detection. Brave + Safari ITP + Firefox strict mode all routinely
     block third-party cookies, which kills the OAuth dance silently.
     We do a cheap storage-write probe; failure surfaces a soft warning
     that points the rep to magic link (works without third-party
     cookies). True negative on first paint = silently hidden. */
  const [cookieWarning, setCookieWarning] = useState(false);
  useEffect(() => {
    try {
      if (typeof navigator === 'undefined') return;
      /* Storage Access API · Safari ITP exposes this. If it's present
         AND hasStorageAccess returns false, we have a strong signal
         the OAuth round-trip will get blocked. */
      const nav = navigator as Navigator & { cookieEnabled?: boolean };
      if (nav.cookieEnabled === false) {
        setCookieWarning(true);
        return;
      }
      /* Best-effort probe · try a write to a probe key, read it back. */
      const probe = '__rv_cookie_probe__';
      try {
        document.cookie = `${probe}=1; SameSite=Lax`;
        const found = document.cookie.split('; ').some((p) => p.startsWith(`${probe}=`));
        if (!found) setCookieWarning(true);
        /* Clean up · expire immediately. */
        document.cookie = `${probe}=; Max-Age=0; SameSite=Lax`;
      } catch { setCookieWarning(true); }
    } catch { /* navigator absent · skip */ }
  }, []);

  /* 2026-07-13 · in-app browser (embedded webview) guard. Google returns
     "Error 403: disallowed_useragent" for OAuth started inside Instagram /
     Facebook / LinkedIn / TikTok / Gmail-app style webviews · a hard dead
     end with no way back into the flow. Detect the webview up front,
     surface a banner with an escape hatch, and gate startGoogleSignIn so
     we never redirect a rep into that wall. The magic-link path never
     touches Google and keeps working in-place, so it is the in-webview
     recommendation. The installed home-screen PWA is explicitly excluded
     by the detector (standalone mode). */
  /* iOS shell · the detector sees OUR OWN Capacitor WKWebView as an in-app
     browser and would tell the rep (or an App Review reviewer) to leave the
     app for Safari. Inside the shell the warning is wrong by construction. */
  const inAppInfo = useMemo(
    () => (nativeIOS ? ({ inApp: false, app: null, platform: 'ios' } as ReturnType<typeof detectInAppBrowser>) : detectInAppBrowser()),
    [nativeIOS],
  );
  const [inAppEscapeTried, setInAppEscapeTried] = useState(false);
  const [inAppLinkCopied, setInAppLinkCopied] = useState(false);
  useEffect(() => {
    if (inAppInfo.inApp) {
      wedgeTrack('login_inapp_browser_detected', {
        app: inAppInfo.app || 'unknown',
        platform: inAppInfo.platform,
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const escapeInAppBrowser = () => {
    wedgeTrack('login_inapp_escape_click', {
      app: inAppInfo.app || 'unknown',
      platform: inAppInfo.platform,
    });
    tryOpenInSystemBrowser(inAppInfo);
    setInAppEscapeTried(true);
  };
  const copyLandingLink = () => {
    try {
      const href = window.location.href;
      navigator.clipboard?.writeText(href).then(
        () => setInAppLinkCopied(true),
        () => setInAppLinkCopied(false),
      );
    } catch { /* clipboard blocked · manual copy from the address bar */ }
  };

  /* 2026-06-02 · round 20 · W10 · last-used method memory. Read once on
     mount · used to reorder buttons so the rep's previously-successful
     path renders prominent. */
  const lastMethod = useMemo<'google' | 'microsoft' | 'apple' | 'magic' | ''>(() => {
    try {
      if (typeof localStorage === 'undefined') return '';
      const v = localStorage.getItem(LAST_METHOD_KEY) || '';
      if (v === 'google' || v === 'microsoft' || v === 'apple' || v === 'magic') return v;
      return '';
    } catch { return ''; }
  }, []);
  const rememberMethod = (method: 'google' | 'microsoft' | 'apple' | 'magic') => {
    try {
      if (typeof localStorage === 'undefined') return;
      localStorage.setItem(LAST_METHOD_KEY, method);
    } catch { /* skip */ }
  };

  /* 2026-06-02 · round 20 · W1 · typo correction. If the email's domain
     is in the typo map, surface a "Did you mean ...?" affordance. The
     rep taps to accept · we never silently change input. */
  const typoSuggestion = useMemo(() => {
    const at = email.indexOf('@');
    if (at < 0) return null;
    const domain = email.slice(at + 1).toLowerCase().trim();
    if (!domain || !(domain in DOMAIN_TYPOS)) return null;
    const local = email.slice(0, at);
    return `${local}@${DOMAIN_TYPOS[domain]}`;
  }, [email]);

  /* 2026-06-02 · round 20 · W5 · normalize on blur · lowercase + trim. */
  const normalizeEmailNow = () => {
    const t = email.trim().toLowerCase();
    if (t !== email) setEmail(t);
  };

  /* U3 · resend cooldown counter. Starts ticking the moment we land in
     the "sent" state and re-arms when the rep taps "Resend". */
  const [cooldown, setCooldown] = useState(0);
  const cooldownTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (magicState === 'sent' && cooldown === 0) setCooldown(RESEND_COOLDOWN_SECONDS);
  }, [magicState]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (cooldown <= 0) {
      if (cooldownTimer.current) { clearInterval(cooldownTimer.current); cooldownTimer.current = null; }
      return;
    }
    if (cooldownTimer.current) return;
    cooldownTimer.current = setInterval(() => {
      setCooldown((c) => (c <= 1 ? 0 : c - 1));
    }, 1000);
    return () => {
      if (cooldownTimer.current) { clearInterval(cooldownTimer.current); cooldownTimer.current = null; }
    };
  }, [cooldown]);

  /* U2 · domain typeahead. Pull common domains + any domain the rep
     previously used (from KNOWN_DOMAINS_KEY · we remember per-device so
     a corp domain typed once auto-completes thereafter). Suggestions
     update live as the user types past the @. */
  const knownDomains = useMemo<string[]>(() => {
    try {
      if (typeof localStorage === 'undefined') return DEFAULT_DOMAINS;
      const raw = localStorage.getItem(KNOWN_DOMAINS_KEY);
      const list = raw ? (JSON.parse(raw) as unknown) : [];
      const arr = Array.isArray(list) ? list.filter((s): s is string => typeof s === 'string') : [];
      /* De-dupe preserving order, prepend the known list */
      const seen = new Set<string>();
      const merged: string[] = [];
      for (const d of [...arr, ...DEFAULT_DOMAINS]) {
        const norm = d.toLowerCase().trim();
        if (!norm || seen.has(norm)) continue;
        seen.add(norm);
        merged.push(norm);
      }
      return merged;
    } catch {
      return DEFAULT_DOMAINS;
    }
  }, []);

  const typeaheadSuggestions = useMemo<string[]>(() => {
    const at = email.indexOf('@');
    if (at < 0) return [];
    const local = email.slice(0, at);
    if (!local) return [];
    const partial = email.slice(at + 1).toLowerCase();
    return knownDomains
      .filter((d) => d.startsWith(partial) && d !== partial)
      .slice(0, 4)
      .map((d) => `${local}@${d}`);
  }, [email, knownDomains]);

  const acceptSuggestion = (suggestion: string) => {
    setEmail(suggestion);
    setEmailTouched(true);
  };

  /* U5 · email validation. Touched=blurred OR send-attempted; never on
     every keystroke. Valid format = green check, invalid = soft amber. */
  const emailLooksValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

  /* Persist last-used email + the email's domain into localStorage on
     successful send so U1 and U2 work for subsequent visits. */
  const rememberEmail = (value: string) => {
    try {
      if (typeof localStorage === 'undefined') return;
      localStorage.setItem(LAST_EMAIL_KEY, value);
      const at = value.indexOf('@');
      if (at > 0) {
        const domain = value.slice(at + 1).toLowerCase();
        if (domain && !DEFAULT_DOMAINS.includes(domain)) {
          const raw = localStorage.getItem(KNOWN_DOMAINS_KEY);
          const list = raw ? (JSON.parse(raw) as unknown) : [];
          const arr = Array.isArray(list) ? list.filter((s): s is string => typeof s === 'string') : [];
          if (!arr.includes(domain)) {
            const next = [domain, ...arr].slice(0, 8);
            localStorage.setItem(KNOWN_DOMAINS_KEY, JSON.stringify(next));
          }
        }
      }
    } catch {
      /* storage blocked · skip silently */
    }
  };

  /* Magic-link send. Shared code path for the form submit, the "Resend"
     button in the success state, and the auto-fire on ?autosend=1. */
  const sendMagicLink = async (value?: string) => {
    const target = (value ?? email).trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(target)) {
      setEmailTouched(true);
      setMagicError('Enter a valid email address');
      setMagicErrorBoth(false);
      setMagicState('error');
      return;
    }
    setMagicError('');
    setMagicErrorBoth(false);
    setMagicState('sending');
    recordLoginAttempt();
    lastAttemptedEmail.current = target;
    /* W4 · offline · skip the fetch entirely · network will reject
       immediately anyway and a clear "you're offline" beats a generic
       error. The online listener will auto-retry when we come back. */
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setMagicState('error');
      setMagicError('You appear to be offline. We will retry the moment you reconnect.');
      return;
    }
    try {
      const res = await fetch(apiUrl('/api/auth/wedge-access/email-me'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: target, ...(returnTo ? { returnTo } : {}) }),
      });
      if (!res.ok) {
        if (res.status === 503) {
          setMagicState('error');
          setMagicErrorBoth(true);
          setMagicError('Email is having trouble. Try Continue with Google instead.');
          return;
        }
        throw new Error(String(res.status));
      }
      rememberEmail(target);
      rememberMethod('magic');
      setMagicState('sent');
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch {
      setMagicState('error');
      setMagicError('Something went wrong. Try Continue with Google or try again.');
    }
  };

  /* U9 · ?email= + (?autosend=1 OR ?send=1) auto-fires the magic-link
     send on mount. Skips when email is already invalid or when the page
     mounted from a deep-link that should be handled upstream (?magic=,
     ?code=+?email=). */
  const autoFiredRef = useRef(false);
  useEffect(() => {
    if (autoFiredRef.current) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const wantsAutoSend = params.get('autosend') === '1' || params.get('send') === '1';
      if (!wantsAutoSend) return;
      const emailParam = (params.get('email') || '').trim().toLowerCase();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailParam)) return;
      autoFiredRef.current = true;
      setEmail(emailParam);
      void sendMagicLink(emailParam);
    } catch {
      /* navigator absent · skip */
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startGoogleSignIn = () => {
    if (googleState !== 'idle') return;
    /* In-app webview · Google would 403 with disallowed_useragent. Never
       start the redirect; fire the system-browser escape instead. The
       banner above the button explains what happened. */
    if (inAppInfo.inApp) {
      wedgeTrack('login_inapp_google_blocked', {
        app: inAppInfo.app || 'unknown',
        platform: inAppInfo.platform,
      });
      escapeInAppBrowser();
      return;
    }
    rememberMethod('google');
    setGoogleState('redirecting');
    /* Carry ?return= through the OAuth bounce · backend appends it to
       the post-callback redirect via state cache (round 19 B1). */
    const base = `${apiUrl(API.AUTH.GOOGLE)}?app=wedge`;
    /* 2026-06-02 · round 22 · I7 · retry-only prompt=consent. We track
       attempt timestamps in GOOGLE_ATTEMPTS_KEY. If the rep is currently
       on an auth_failed redirect bounce (initialError set) OR has tried
       Google within the last 60s, append &prompt=consent to force a
       fresh consent + account picker. First attempt stays clean so the
       round 21 phone-400 regression cannot reappear. */
    let promptHint = '';
    try {
      if (typeof localStorage !== 'undefined') {
        const now = Date.now();
        const raw = localStorage.getItem(GOOGLE_ATTEMPTS_KEY);
        const list = raw ? (JSON.parse(raw) as unknown) : [];
        const recent = Array.isArray(list)
          ? list.filter((x): x is number => typeof x === 'number' && now - x < 60_000)
          : [];
        const onRetry = (initialError && initialError === 'auth_failed') || recent.length > 0;
        if (onRetry) promptHint = '&prompt=consent';
        const next = [now, ...recent].slice(0, 5);
        localStorage.setItem(GOOGLE_ATTEMPTS_KEY, JSON.stringify(next));
      }
    } catch { /* storage blocked · skip the retry hint */ }
    const ret = returnTo ? `&return=${encodeURIComponent(returnTo)}` : '';
    const url = `${base}${ret}${promptHint}`;
    window.location.assign(url);
  };
  const startMicrosoftSignIn = async () => {
    if (msState !== 'idle') return;
    rememberMethod('microsoft');
    setMsState('redirecting');
    /* Native iOS: the web redirect sets a cookie the Bearer-based shell can't
       use, so run the installed-app PKCE flow and POST the id_token to the
       native credential endpoint (mirrors the Google/Apple native paths). Web
       keeps the redirect below. */
    if (isNativeIOS() && (await microsoftNativeAvailable())) {
      try {
        const r = await signInWithMicrosoft();
        if (!r?.idToken) { setMsState('idle'); return; } // cancelled / plugin absent
        const res = await fetch(apiUrl('/api/auth/microsoft/credential-native'), {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idToken: r.idToken }),
        });
        if (!res.ok) { setMsState('idle'); return; }
        const data = (await res.json()) as { isNewUser?: boolean; accessToken?: string; refreshToken?: string };
        try { localStorage.removeItem('rv_auth_user_cache'); } catch { /* noop */ }
        if (data.accessToken || data.refreshToken) setWedgeTokens(data.accessToken, data.refreshToken);
        const params = new URLSearchParams();
        if (data.isNewUser) params.set('fresh', '1');
        if (returnTo) params.set('return', returnTo);
        const qs = params.toString();
        window.location.assign(`/auth/callback${qs ? `?${qs}` : ''}`);
      } catch { setMsState('idle'); }
      return;
    }
    const base = `${apiUrl('/api/auth/microsoft')}?app=wedge`;
    const url = returnTo ? `${base}&return=${encodeURIComponent(returnTo)}` : base;
    window.location.assign(url);
  };
  /* Sign in with Apple (App Store Guideline 4.8). This is the WEB redirect
     path: bounce to the backend /api/auth/apple start route, which redirects to
     Apple and form_posts back to /api/auth/apple/callback. The native iOS build
     uses the Capacitor apple-sign-in plugin instead, POSTing the identity_token
     straight to /api/auth/apple/credential · same backend provisioning lane. */
  const startAppleSignIn = () => {
    if (appleState !== 'idle') return;
    rememberMethod('apple');
    setAppleState('redirecting');
    const base = `${apiUrl('/api/auth/apple')}?app=wedge`;
    const url = returnTo ? `${base}&return=${encodeURIComponent(returnTo)}` : base;
    window.location.assign(url);
  };
  /* NATIVE Apple flow (iOS shell only). The Capacitor plugin's sheet returns
     an Apple identity token; the backend verifies it against Apple's JWKS at
     POST /api/auth/apple/credential and returns session tokens in the BODY,
     because the shell's capacitor://localhost origin never carries the
     sameSite=lax cookies. We bootstrap the wedge standalone token bridge
     (setWedgeTokens · same mechanism as the iOS-PWA cookie-loss path), then
     load /auth/callback so AuthCallbackWedge runs the identical session
     establishment: fresh=1 wipe + signup tracking for brand-new users
     (onboarding is never skipped), bootstrapWedgeAuth, and the route in. */
  const startAppleSignInNative = async () => {
    if (appleState !== 'idle') return;
    setAppleError('');
    setAppleState('authorizing');
    try {
      const r = await signInWithApple();
      if (!r?.identityToken) throw new Error('no_identity_token');
      const res = await fetch(apiUrl('/api/auth/apple/credential'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identityToken: r.identityToken,
          /* authorizationCode lets the backend mint an Apple refresh token so
             account deletion can revoke the Sign in with Apple grant
             (Guideline 5.1.1(v)). Optional server-side; sign-in never depends
             on it. */
          ...(r.authorizationCode ? { authorizationCode: r.authorizationCode } : {}),
          ...(r.givenName ? { givenName: r.givenName } : {}),
          ...(r.familyName ? { familyName: r.familyName } : {}),
        }),
      });
      if (!res.ok) {
        setAppleState('idle');
        /* apple_email_hidden · the rep previously authorized Apple for this app
           under an account that no longer exists, so Apple withholds the email
           and there is nothing server-side to recover it from. Retrying does
           nothing; the rep has to reset the grant in Settings first. */
        let code = '';
        try {
          const body = (await res.json()) as { code?: unknown };
          code = typeof body?.code === 'string' ? body.code : '';
        } catch {
          /* no JSON body · fall through to the generic message */
        }
        if (code === 'apple_email_hidden') {
          /* Bug 9A · remember the orphaned grant so the screen leads with the
             alternate paths (native Google · magic link · PIN) instead of the
             same Apple button that just failed. One-shot · cleared on the next
             successful sign-in. */
          try { sessionStorage.setItem(APPLE_GRANT_ORPHAN_KEY, '1'); } catch { /* storage blocked · skip */ }
          setAppleGrantOrphan(true);
          setAppleError(APPLE_EMAIL_HIDDEN_MSG);
        } else {
          setAppleError('Apple sign-in did not complete. Try again in a moment.');
        }
        return;
      }
      const data = (await res.json()) as { ok?: boolean; isNewUser?: boolean; accessToken?: string; refreshToken?: string };
      /* 07-27 remediation audit · sign-in = possible identity swap: drop the
         previous rep's offline session cache before the new tokens land
         (setWedgeTokens no longer clears it · it also runs on every routine
         token rotation). */
      try { localStorage.removeItem('rv_auth_user_cache'); } catch { /* noop */ }
      if (data.accessToken || data.refreshToken) setWedgeTokens(data.accessToken, data.refreshToken);
      rememberMethod('apple');
      /* Bug 9A · Apple returned an email · the grant is healthy again. */
      clearAppleGrantOrphan();
      const params = new URLSearchParams();
      if (data.isNewUser) params.set('fresh', '1');
      if (returnTo) params.set('return', returnTo);
      const qs = params.toString();
      window.location.assign(`/auth/callback${qs ? `?${qs}` : ''}`);
    } catch (err) {
      setAppleState('idle');
      const msg = err instanceof Error ? err.message : String(err);
      /* The rep dismissing the native Apple sheet is not an error state
         (ASAuthorizationError.canceled = code 1001). */
      if (/cancel|1001/i.test(msg)) return;
      setAppleError('Apple sign-in did not complete. Try again in a moment.');
    }
  };

  /* 2026-08-04 (founder) · native Google sign-in, mirrors startAppleSignInNative.
     The native GoogleAuth plugin returns an ID token; we POST it to the native
     credential endpoint which returns session tokens in the BODY (the WKWebView
     cannot use the cross-site cookies), bootstrap them via setWedgeTokens, and
     route to /auth/callback. Email-keyed on the backend, so a rep who already
     signed up with Apple/PIN on the same email LINKS to the same account. */
  const startGoogleSignInNative = async () => {
    if (googleNativeState !== 'idle') return;
    setGoogleNativeError('');
    setGoogleNativeState('authorizing');
    try {
      const r = await signInWithGoogle();
      if (!r?.idToken) { setGoogleNativeState('idle'); return; } // cancelled or plugin absent · no error surface
      const res = await fetch(apiUrl('/api/auth/google/credential-native'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: r.idToken }),
      });
      if (!res.ok) {
        setGoogleNativeState('idle');
        setGoogleNativeError('Google sign-in did not complete. Try again in a moment.');
        return;
      }
      const data = (await res.json()) as { ok?: boolean; isNewUser?: boolean; accessToken?: string; refreshToken?: string };
      /* drop the previous rep's offline session cache before the new tokens land
         (sign-in = possible identity swap), matching the Apple native path. */
      try { localStorage.removeItem('rv_auth_user_cache'); } catch { /* noop */ }
      if (data.accessToken || data.refreshToken) setWedgeTokens(data.accessToken, data.refreshToken);
      rememberMethod('google');
      /* Bug 9A · rep recovered via native Google · stop leading with the
         Apple orphan affordance. */
      clearAppleGrantOrphan();
      const params = new URLSearchParams();
      if (data.isNewUser) params.set('fresh', '1');
      if (returnTo) params.set('return', returnTo);
      const qs = params.toString();
      window.location.assign(`/auth/callback${qs ? `?${qs}` : ''}`);
    } catch (err) {
      setGoogleNativeState('idle');
      const msg = err instanceof Error ? err.message : String(err);
      if (/cancel/i.test(msg)) return; // rep dismissed the Google sheet · not an error
      setGoogleNativeError('Google sign-in did not complete. Try again in a moment.');
    }
  };

  /* 2026-07-27 · native-only PIN recovery submit. Posts to the SAME
     sign-in-link route CodeRedeem's "Lost access?" flow uses · no new
     endpoint, no new validation surface, no backend change. The response is
     always ok for a well-formed address, so we move to the neutral 'sent'
     state and never say whether the account exists. */
  const submitRecovery = async () => {
    if (recoverState === 'sending') return;
    setRecoverError('');
    const e = recoverEmail.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) {
      setRecoverError('Enter the email you use for RepVantage.');
      return;
    }
    /* Offline · skip the fetch the same way sendMagicLink does. The network
       would reject immediately anyway and "you appear to be offline" beats a
       generic failure. */
    if (!isOnline || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
      setRecoverError('You appear to be offline. Reconnect and try again.');
      setRecoverState('idle');
      return;
    }
    setRecoverState('sending');
    try {
      const res = await fetch(apiUrl('/api/auth/wedge-access/sign-in-link'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: e }),
      });
      if (!res.ok) {
        /* NEVER surface the server's raw `message`. The route is
           @Throttle({ limit: 6, ttl: 900000 }) and the service returns ok for
           unknown emails by design, so 429 is effectively the only reachable
           non-ok status · rendering the framework string would put
           "ThrottlerException: Too Many Requests" in a red box on the rep's
           phone. Map to human copy the way sendMagicLink already does. */
        setRecoverState('idle');
        if (res.status === 429) {
          setRecoverError('Too many tries. Wait a few minutes, or use Continue with Apple to get in now.');
        } else if (res.status === 400) {
          setRecoverError('That email address does not look right. Check it and try again.');
        } else if (res.status === 503) {
          setRecoverError('Email is having trouble right now. Use Continue with Apple, or try again shortly.');
        } else {
          setRecoverError('Could not send the link. Try again, or use Continue with Apple.');
        }
        return;
      }
      setRecoverState('sent');
    } catch {
      setRecoverState('idle');
      setRecoverError('Could not send the link. Check your connection and try again.');
    }
  };

  const screen: CSSProperties = {
    // 2026-08-04 (founder) · on the native app the sign-in is locked to ONE
    // page: fill the dynamic viewport (dvh accounts for the iOS browser chrome)
    // and clip any overflow so it never scrolls, uniform on every device size.
    // Web keeps minHeight:100vh so the marketing landing (rails + more content
    // below) still scrolls normally.
    ...(nativeIOS
      ? { height: '100dvh', maxHeight: '100dvh', overflow: 'hidden' }
      : { minHeight: '100vh' }),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: nativeIOS ? '16px 20px' : '32px 20px',
    // Matches repvantage.io exactly: GREEN linear-gradient base + strong 0.20
    // emerald radial + faint amber spot. The old flat #0A1A10 base read as black.
    background:
      BRAND.screenBg,
    color: BRAND.text,
    fontFamily: BRAND.fontBody,
    boxSizing: 'border-box',
  };

  const brandMark = (
    <div
      data-wedge-animated
      style={{
        position: 'relative',
        // 2026-08-04 (founder · one-page no-scroll native lock) · the radar is
        // trimmed on native so the wordmark + tagline + radar + both sign-in
        // buttons + links all fit one viewport without scrolling on the
        // smallest phone. Web keeps the larger mark.
        width: nativeIOS ? 'clamp(132px, 32vw, 184px)' : 'clamp(176px, 40vw, 240px)',
        height: nativeIOS ? 'clamp(132px, 32vw, 184px)' : 'clamp(176px, 40vw, 240px)',
        margin: nativeIOS ? '0 auto 8px' : '0 auto 14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'wedgeEnergize 980ms cubic-bezier(0.16, 1, 0.3, 1) 80ms backwards',
      }}
    >
      {/* Expanding sonar rings replace the single conic sweep. Resting opacity
          0.22 means a reduced-motion (animation-off) render shows three static
          concentric emerald rings, never blank. */}
      {[0, 0.9, 1.8].map((delay) => (
        <div
          key={delay}
          aria-hidden
          style={{
            position: 'absolute',
            width: '42%',
            height: '42%',
            borderRadius: '50%',
            border: '1.5px solid rgba(52,211,153,0.55)',
            boxShadow: '0 0 18px rgba(52,211,153,0.30)',
            opacity: 0.22,
            pointerEvents: 'none',
            animation: `wedgeSonar 2.9s ease-out ${delay + 1.2}s infinite`,
          }}
        />
      ))}
      {/* Faint crosshair reticle. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: '14%',
          backgroundImage:
            'linear-gradient(rgba(52,211,153,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(52,211,153,0.16) 1px, transparent 1px)',
          backgroundSize: '50% 50%',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          maskImage: 'radial-gradient(circle at center, black 30%, transparent 72%)',
          WebkitMaskImage: 'radial-gradient(circle at center, black 30%, transparent 72%)',
          pointerEvents: 'none',
        }}
      />
      {/* Center glow + amber-core radar mark (unchanged asset). */}
      <div
        style={{
          position: 'absolute',
          width: '54%',
          height: '54%',
          borderRadius: '50%',
          background:
            'radial-gradient(circle at 38% 32%, rgba(52,211,153,0.28), rgba(10,26,16,0) 72%)',
          boxShadow:
            '0 0 0 1px rgba(52,211,153,0.28), inset 0 0 24px rgba(52,211,153,0.10), 0 0 56px rgba(52,211,153,0.22)',
        }}
      />
      <img
        src="/wedge/repvantage-radar.svg?v=20260712"
        alt="RepVantage"
        style={{
          position: 'relative',
          zIndex: 1,
          width: '46%',
          height: '46%',
          filter: 'drop-shadow(0 2px 10px rgba(52,211,153,0.30))',
        }}
      />
      {/* Four corner HUD ticks. */}
      {[
        { top: 0, left: 0, borderTop: '1.5px solid rgba(52,211,153,0.5)', borderLeft: '1.5px solid rgba(52,211,153,0.5)' },
        { top: 0, right: 0, borderTop: '1.5px solid rgba(52,211,153,0.5)', borderRight: '1.5px solid rgba(52,211,153,0.5)' },
        { bottom: 0, left: 0, borderBottom: '1.5px solid rgba(52,211,153,0.5)', borderLeft: '1.5px solid rgba(52,211,153,0.5)' },
        { bottom: 0, right: 0, borderBottom: '1.5px solid rgba(52,211,153,0.5)', borderRight: '1.5px solid rgba(52,211,153,0.5)' },
      ].map((c, i) => (
        <div key={i} aria-hidden style={{ position: 'absolute', width: 16, height: 16, ...c }} />
      ))}
    </div>
  );

  const wordmark = (
    <div
      style={{
        fontFamily: BRAND.fontDisplay,
        // 2026-08-04 (founder) · the wordmark is now the DOMINANT brand element,
        // larger than the "Walk in knowing" headline below it. Clamp keeps it
        // from overflowing the narrowest phone; letterSpacing tightened from 4
        // to 2 so the bigger size still fits "REPVANTAGE" on one line.
        fontSize: 'clamp(1.9rem, 8vw, 2.9rem)',
        fontWeight: 800,
        letterSpacing: 2,
        textTransform: 'uppercase',
        marginBottom: 4,
        textAlign: 'center',
        whiteSpace: 'nowrap',
        animation: 'wedgeStagger 520ms cubic-bezier(0.16, 1, 0.3, 1) 160ms backwards',
      }}
    >
      <span style={{ color: '#F0F0F0' }}>REP</span>
      <span style={{ color: BRAND.amber }}>VANTAGE</span>
    </div>
  );

  const mainHeadline = (
    <>
      {/* Orbitron display line promoted ABOVE the radar for the main sign-in
          view (per-character cascade + emerald glow, values match the
          OnboardValue splash headline for cohesion). Rendered only for
          view==='main' via the header conditional below. */}
      <div
        data-wedge-animated
        style={{
          fontFamily: BRAND.fontDisplay,
          // 2026-08-04 (founder) · trimmed so the wordmark above is clearly the
          // larger element · the tagline now reads as a subordinate line.
          fontSize: 'clamp(1.2rem, 4vw, 1.75rem)',
          fontWeight: 800,
          letterSpacing: 0,
          lineHeight: 1.05,
          color: '#F4F5F7',
          perspective: 600,
          marginTop: 2,
          WebkitTextStroke: '0.4px rgba(52,211,153,0.14)',
          textShadow: '0 0 18px rgba(52,211,153,0.35), 0 0 42px rgba(52,211,153,0.16), 0 1px 24px rgba(0,0,0,0.6)',
        }}
      >
        {'Walk in knowing'.split(' ').flatMap((word, wi, words) => {
          const off = words.slice(0, wi).reduce((a, w) => a + w.length, 0) + wi;
          const wordEl = (
            <span key={`w${wi}`} style={{ display: 'inline-block' }}>
              {[...word].map((ch, ci) => (
                <span
                  key={ci}
                  style={{
                    display: 'inline-block',
                    animation: `wedgeCharCascade 520ms cubic-bezier(0.16, 1, 0.3, 1) ${360 + (off + ci) * 22}ms backwards`,
                  }}
                >
                  {ch}
                </span>
              ))}
            </span>
          );
          return wi < words.length - 1 ? [wordEl, ' '] : [wordEl];
        })}
      </div>
      {/* Emerald accent line under the headline. */}
      <div
        data-wedge-animated
        aria-hidden
        style={{
          width: 64,
          height: 2,
          borderRadius: 2,
          margin: '12px auto 16px',
          background: '#34D399',
          boxShadow: '0 0 10px rgba(52,211,153,0.75)',
          transformOrigin: 'center',
          animation: 'wedgeLineDraw 540ms cubic-bezier(0.22, 1, 0.36, 1) 900ms backwards',
        }}
      />
    </>
  );

  const styleTag = (
    <style>{`
      @keyframes wedgeFadeUp {
        from { opacity: 0; transform: translateY(14px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes wedgePulse {
        0%, 100% { opacity: 0.85; transform: scale(1); }
        50% { opacity: 0.35; transform: scale(1.06); }
      }
      @keyframes wedgeRadarSweep {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes wedgeLiveDot {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.3; }
      }
      @keyframes wedgeSpin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      /* 2026-07-31 cohesive-entrance motif, inlined because this landing renders
         outside .wedge-scope and never receives WEDGE_SCOPED_CSS. Values mirror
         OnboardValue (prep-screens-1.jsx) and wedgeStyles.ts. */
      @keyframes wedgeStagger {
        from { opacity: 0; transform: translateY(12px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes wedgeCharCascade {
        from { opacity: 0; transform: translateY(14px) rotateX(-50deg); filter: blur(4px); }
        to { opacity: 1; transform: translateY(0) rotateX(0); filter: blur(0); }
      }
      @keyframes wedgeSonar {
        0% { transform: scale(0.4); opacity: 0; }
        8% { opacity: 0.5; }
        100% { transform: scale(2.6); opacity: 0; }
      }
      @keyframes wedgeEnergize {
        from { opacity: 0; transform: scale(0.72) rotate(-14deg); filter: blur(6px); }
        70% { filter: blur(0); }
        to { opacity: 1; transform: scale(1) rotate(0); filter: blur(0); }
      }
      @keyframes wedgeLineDraw {
        from { transform: scaleX(0); opacity: 0; }
        20% { opacity: 1; }
        to { transform: scaleX(1); opacity: 1; }
      }
      @keyframes wedgeGridDrift {
        0% { background-position: 0 0; }
        100% { background-position: 0 40px; }
      }
      @keyframes wedgeAuroraBreath {
        0%, 100% { opacity: 0.55; transform: translate(-50%, -50%) scale(1); }
        50% { opacity: 0.85; transform: translate(-50%, -50%) scale(1.08); }
      }
      /* 2026-08-20 (founder · Fable gateway) · web-only atmosphere + status bar.
         All additive, behind the card, reduced-motion guarded. Nothing here
         alters a control or handler. */
      @keyframes wedgeBlip {
        0% { transform: scale(0.35); opacity: 0.55; }
        100% { transform: scale(2.4); opacity: 0; }
      }
      @keyframes wedgeGlowBreath {
        0%, 100% { opacity: 0.5; }
        50% { opacity: 0.95; }
      }
      @keyframes wedgeShimmer {
        0% { transform: translateX(-130%); }
        100% { transform: translateX(130%); }
      }

      /* numbered rail badges (01..) + comp's slide-on-hover */
      .wedge-linkrail-left { counter-reset: rvrail 0; }
      .wedge-linkrail-right { counter-reset: rvrail 4; }
      .wedge-railcard { position: relative; counter-increment: rvrail; }
      .wedge-railcard::after {
        content: counter(rvrail, decimal-leading-zero);
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 8px;
        border: 1px solid rgba(52,211,153,0.28);
        background: rgba(52,211,153,0.08);
        font-family: 'Share Tech Mono', ui-monospace, monospace;
        font-size: 12px;
        color: #6EE7B7;
      }
      .wedge-linkrail-left .wedge-railcard { padding-right: 46px; }
      .wedge-linkrail-left .wedge-railcard::after { right: 12px; }
      .wedge-linkrail-right .wedge-railcard { padding-left: 46px; }
      .wedge-linkrail-right .wedge-railcard::after { left: 12px; }
      .wedge-linkrail-left .wedge-railcard:hover { transform: translateX(6px); }
      .wedge-linkrail-right .wedge-railcard:hover { transform: translateX(-6px); }
      /* the money-lane accent · first Resources card (Pricing) reads as $ */
      /* Pricing reads as the money lane · gold $ badge instead of a number, and
         it does not advance the rail counter (so the rest stay sequential). */
      .wedge-railcard-money { counter-increment: none; }
      .wedge-railcard-money::after {
        content: '$';
        border-color: rgba(245,158,11,0.4);
        background: rgba(245,158,11,0.10);
        color: #FBBF24;
        font-size: 14px;
      }
      /* the AES-256 status bar (>=720px web) carries Privacy/Terms, so drop the
         centered bottom row wherever the status bar shows. Narrow web (<720px)
         and native iOS (no status bar) keep the centered row. */
      @media (min-width: 720px) {
        .wedge-landing-screen:not(.wedge-landing-ios) .wedge-signin-footer { display: none !important; }
        /* the status bar is the last in-flow row (see below), flush to the
           bottom edge, so the screen needs no bottom reserve for it. */
        .wedge-landing-screen:not(.wedge-landing-ios) { padding-bottom: 0 !important; }
      }
      /* bottom status bar · desktop web only
         2026-09-30 · no longer position:fixed. Whenever the page was taller than
         the viewport (641px to 1079px wide, or a short desktop window) the fixed
         bar sat on top of the quick-link pills and the centered
         "wedge.repvantage.io" line, which drew across SECURE CHANNEL and the
         social cluster. The bar is now the last row in normal flow, full bleed
         (-20px cancels the screen's side padding), so nothing can scroll under
         it. The empty ::before and the bar's auto top margin split the spare
         height, and the domain line's 23px bottom margin (64px old reserve minus
         the 41px bar) keeps the hero exactly where the fixed bar left it, with
         the bar on the viewport bottom edge, whenever everything fits. */
      .wedge-statusbar { display: none; }
      @media (min-width: 720px) {
        .wedge-landing-screen:not(.wedge-landing-ios)::before {
          content: '';
          flex: none;
          margin-bottom: auto;
        }
        .wedge-landing-screen:not(.wedge-landing-ios) .wedge-domain-line { margin-bottom: 23px; }
        .wedge-landing-screen:not(.wedge-landing-ios) .wedge-statusbar {
          align-self: stretch;
          flex: none;
          margin: auto -20px 0;
          position: relative;
          z-index: 3;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 12px 28px;
          border-top: 1px solid rgba(255,255,255,0.05);
          font-family: 'Share Tech Mono', ui-monospace, monospace;
          font-size: 12px;
          letter-spacing: 1.2px;
          color: #8494A8;
          pointer-events: auto;
          background: linear-gradient(0deg, rgba(10,26,16,0.82), rgba(10,26,16,0));
        }
      }
      .wedge-statusbar a { color: #94A3B8; display: inline-flex; transition: color 150ms ease, transform 150ms ease; }
      .wedge-statusbar a:hover { color: #6EE7B7; transform: translateY(-2px); }
      /* responsive · drop the center status readout before it can crowd the
         Privacy/Terms and social clusters on tablet-width status bars. */
      @media (max-width: 1000px) { .wedge-status-mid { display: none !important; } }
      /* phones · the decorative $ signal blips are hidden so the single-column
         form reads clean (the giant radar + grid stay as the quiet backdrop). */
      @media (max-width: 719px) { .wedge-blip { display: none !important; } }
      .wedge-spinner {
        width: 14px;
        height: 14px;
        border-radius: 50%;
        border: 1.6px solid rgba(0,0,0,0.18);
        border-top-color: #1f1f1f;
        animation: wedgeSpin 0.8s linear infinite;
        display: inline-block;
      }
      .wedge-spinner-light {
        width: 14px;
        height: 14px;
        border-radius: 50%;
        border: 1.6px solid rgba(255,255,255,0.22);
        border-top-color: #ffffff;
        animation: wedgeSpin 0.8s linear infinite;
        display: inline-block;
      }
      .wedge-linkrail {
        position: fixed;
        top: 50%;
        transform: translateY(-50%);
        z-index: 1;
        width: 208px;
        display: none;
        pointer-events: auto;
      }
      .wedge-linkrail-left { left: max(24px, calc(50% - 528px)); text-align: left; }
      .wedge-linkrail-right { right: max(24px, calc(50% - 528px)); text-align: right; }
      .wedge-linkrail-title {
        font-family: 'Share Tech Mono', ui-monospace, monospace;
        font-size: 30px;
        letter-spacing: 1.5px;
        text-transform: uppercase;
        color: #F59E0B;
        opacity: 1;
        margin-bottom: 14px;
      }
      .wedge-railcard {
        display: block;
        text-decoration: none;
        padding: 11px 13px;
        margin-bottom: 10px;
        border-radius: 12px;
        border: 1px solid rgba(52,211,153,0.14);
        background: rgba(52,211,153,0.04);
        transition: border-color 140ms ease, background 140ms ease, transform 140ms ease;
      }
      .wedge-railcard:hover {
        border-color: rgba(52,211,153,0.42);
        background: rgba(52,211,153,0.09);
        transform: translateY(-1px);
      }
      .wedge-railcard-label { font-size: 13.5px; font-weight: 600; color: #E2E8F0; }
      .wedge-railcard-sub { font-size: 12px; color: #94A3B8; margin-top: 2px; }
      .wedge-linkchips {
        margin-top: 16px;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        justify-content: center;
      }
      .wedge-linkchip {
        text-decoration: none;
        font-size: 12px;
        font-weight: 600;
        color: #CBD5E1;
        padding: 8px 13px;
        border-radius: 999px;
        border: 1px solid rgba(52,211,153,0.16);
        background: rgba(52,211,153,0.05);
        display: inline-flex;
        align-items: center;
        gap: 5px;
        min-height: 40px;
        box-sizing: border-box;
        transition: border-color 140ms ease, background 140ms ease, color 140ms ease;
      }
      .wedge-linkchip:hover {
        border-color: rgba(52,211,153,0.42);
        background: rgba(52,211,153,0.10);
        color: #ECFDF5;
      }
      @media (min-width: 1080px) {
        .wedge-linkrail { display: block; }
        .wedge-linkchips { display: none; }
      }
      /* 2026-08-09 (founder · desktop web) · "Walk in knowing" tagline moves to
         a fixed top-right mount on desktop web (mirrors the top-left wordmark);
         mobile web + iOS/iPad native keep it in the centered hero card. The
         :not(.wedge-landing-ios) scope keeps iPad-native landscape (>=1080px)
         showing the in-card tagline, so native is untouched. */
      .wedge-tagline-topright { display: none; }
      @media (min-width: 1080px) {
        .wedge-landing-screen:not(.wedge-landing-ios) .wedge-tagline-topright { display: block; }
        .wedge-landing-screen:not(.wedge-landing-ios) .wedge-tagline-incard { display: none; }
      }
      /* 2026-07-31 · on phones the fixed top-left wordmark (top:22, ~32px tall)
         overlapped the centered hero headline. Top-align the hero and clear the
         wordmark. !important is required to beat the inline screen style. */
      @media (max-width: 640px) {
        .wedge-landing-screen { justify-content: flex-start !important; padding-top: 76px !important; }
      }
      /* 2026-08-01 (b56) · native iOS only · the wordmark now sits at
         env(safe-area-inset-top) + 16px instead of top:22, so bump the hero's
         top padding by the same safe-area inset to keep the same clearance and
         never let the wordmark overlap "Walk in knowing". The compound selector
         (0,2,0) outranks the phone rule above regardless of source order. */
      .wedge-landing-screen.wedge-landing-ios { padding-top: calc(env(safe-area-inset-top, 0px) + 76px) !important; }
      /* 2026-08-19 (founder) · phones · web only · the top-left wordmark was
         position:fixed and overlapped the hero on narrow screens. Un-fix it and
         center it in normal flow so it never collides with the tagline, drop the
         76px reserve that only existed to clear the fixed mount, and pin the
         footer to the bottom so no large blank space sits below it. Native iOS
         keeps its own safe-area wordmark mount and padding, untouched.
         2026-09-28 · widened from 640px to 1079px: between 641px and 1079px the
         fixed top-left wordmark sat on top of the centered in-card tagline
         (the top-right tagline mount only starts at 1080px). */
      @media (max-width: 1079px) {
        .wedge-landing-screen:not(.wedge-landing-ios) .wedge-wordmark-mount {
          position: static !important;
          top: auto !important;
          left: auto !important;
          right: auto !important;
          text-align: center;
          margin: 0 auto;
        }
        .wedge-landing-screen:not(.wedge-landing-ios) { padding-top: 20px !important; }
        .wedge-landing-screen:not(.wedge-landing-ios) .wedge-signin-footer { margin-top: auto !important; }
      }
      /* 2026-08-19 (founder) · keychain / password-manager autofill was painting
         the email and password inputs with the browser white fill, breaking the
         dark treatment. Keep the field dark and the text light while autofill
         still works (the inset shadow + long transition is the standard override,
         it never blocks the fill itself). */
      input:-webkit-autofill,
      input:-webkit-autofill:hover,
      input:-webkit-autofill:focus,
      input:-webkit-autofill:active {
        -webkit-text-fill-color: #E2E8F0 !important;
        -webkit-box-shadow: 0 0 0 1000px #0d1a13 inset !important;
        box-shadow: 0 0 0 1000px #0d1a13 inset !important;
        caret-color: #E2E8F0;
        border-radius: 12px;
        transition: background-color 600000s 0s, color 600000s 0s;
      }
      input:autofill {
        -webkit-text-fill-color: #E2E8F0 !important;
        box-shadow: 0 0 0 1000px #0d1a13 inset !important;
        border-radius: 12px;
      }
      @media (prefers-reduced-motion: reduce) {
        [data-wedge-animated], [data-wedge-animated] * { animation: none !important; }
        .wedge-spinner, .wedge-spinner-light { animation: none !important; }

        .wedge-railcard, .wedge-linkchip { transition: none !important; }
        .wedge-railcard:hover,
        .wedge-linkrail-left .wedge-railcard:hover,
        .wedge-linkrail-right .wedge-railcard:hover { transform: none !important; }
      }
    `}</style>
  );

  /* 2026-08-19 · email + password submit (Log in / Sign up). Mirrors the native
     token-bridge flow: POST, bridge body tokens for the iOS shell, then land on
     /auth/callback (session cookie on web, Bearer on native). */
  const submitPassword = async () => {
    const target = email.trim().toLowerCase();
    if (!emailLooksValid) { setPwError('Enter a valid email address.'); return; }
    if (password.length < 8) { setPwError('Password must be at least 8 characters.'); return; }
    setPwError(''); setPwNotice(''); setPwState('submitting');
    const path = authMode === 'signup' ? '/api/auth/wedge/password/register' : '/api/auth/wedge/password/login';
    try {
      const res = await fetch(apiUrl(path), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          authMode === 'signup'
            ? { email: target, password, name: pwName.trim() || undefined }
            : { email: target, password },
        ),
      });
      const data: Record<string, unknown> = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPwState('idle');
        setPwError(
          (typeof data.message === 'string' && data.message) ||
            (authMode === 'signup' ? 'Could not create your account.' : 'That email or password did not match.'),
        );
        return;
      }
      if (data.pendingLink) {
        setPwState('idle');
        setPwNotice((typeof data.message === 'string' && data.message) || 'Check your email for a link to add a password.');
        return;
      }
      if (data.accessToken || data.refreshToken) {
        setWedgeTokens(data.accessToken as string | undefined, data.refreshToken as string | undefined);
      }
      try { localStorage.removeItem('rv_auth_user_cache'); } catch { /* noop */ }
      /* Bug 9A · rep got in via email + password · clear the Apple orphan flag. */
      clearAppleGrantOrphan();
      const qs = new URLSearchParams();
      if (data.isNewUser) qs.set('fresh', '1');
      if (returnTo) qs.set('return', returnTo);
      const q = qs.toString();
      window.location.assign(`/auth/callback${q ? `?${q}` : ''}`);
    } catch {
      setPwState('idle');
      setPwError('Network error. Check your connection and try again.');
    }
  };

  const submitForgot = async () => {
    const target = email.trim().toLowerCase();
    if (!emailLooksValid) { setPwError('Enter your email above first.'); return; }
    setPwError(''); setForgotState('sending');
    try {
      await fetch(apiUrl('/api/auth/wedge/password/forgot'), {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: target }),
      });
    } catch { /* generic · always show 'sent' */ }
    setForgotState('sent');
    setPwNotice(`If an account exists for ${target}, we sent a password reset link.`);
  };


  return (
    <main className={`wedge-landing-screen${nativeIOS ? ' wedge-landing-ios' : ''}`} style={screen}>
      {styleTag}
      {/* 2026-07-31 · RepVantage wordmark moved to a fixed top-left brand
          position (off the centered hero) for the main sign-in view.
          2026-08-01 (b56) · on the native iOS shell the top-left, top:22 mount
          sat UNDER the status bar / notch and was clipped. On native iOS only:
          centre it (left:0/right:0 + the wordmark's own textAlign:center) and
          drop it below the safe area with env(safe-area-inset-top). Web/desktop
          keep the exact top-left placement. The screen padding-top is bumped in
          lockstep (see .wedge-landing-ios) so the hero still clears it. */}
      {view === 'main' && (
        <div className="wedge-wordmark-mount" style={nativeIOS ? {
          position: 'fixed',
          top: 'calc(env(safe-area-inset-top, 0px) + 16px)',
          left: 0,
          right: 0,
          textAlign: 'center',
          zIndex: 2,
        } : { position: 'fixed', top: 22, left: 'max(22px, calc(50% - 660px))', zIndex: 2 }}>
          {wordmark}
        </div>
      )}
      {/* 2026-08-09 (founder · desktop web) · "Walk in knowing" mirrored to a
          fixed top-right mount on desktop web (parallels the top-left wordmark).
          Never renders on native; CSS (.wedge-tagline-topright) reveals it only
          at >=1080px on non-native, and hides the in-card copy there. */}
      {view === 'main' && !nativeIOS && (
        <div className="wedge-tagline-topright" style={{ position: 'fixed', top: 22, right: 'max(22px, calc(50% - 660px))', zIndex: 2, textAlign: 'right' }}>
          {mainHeadline}
        </div>
      )}
      {/* 2026-07-31 ambient entrance bed: emerald perspective grid floor +
          horizon seam + blurred aurora, mirroring the OnboardValue splash so
          sign-in and onboarding read as one sequence. Fixed and behind the
          card, pointer-events none. data-wedge-animated so the reduced-motion
          guard reaches its children, which keep a full static composition. */}
      <div
        data-wedge-animated
        aria-hidden
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '46%',
            width: 'clamp(320px, 78vw, 620px)',
            height: 'clamp(320px, 78vw, 620px)',
            transform: 'translate(-50%, -50%)',
            borderRadius: '50%',
            background:
              'radial-gradient(circle at center, rgba(52,211,153,0.20) 0%, rgba(52,211,153,0.07) 38%, rgba(52,211,153,0) 70%)',
            filter: 'blur(40px)',
            animation: 'wedgeAuroraBreath 12s ease-in-out infinite',
          }}
        />
        <div style={{ position: 'absolute', inset: 0, perspective: '900px', perspectiveOrigin: '50% 30%' }}>
          <div
            style={{
              position: 'absolute',
              left: '-25%',
              right: '-25%',
              top: '58%',
              bottom: '-30%',
              backgroundImage:
                'linear-gradient(rgba(52,211,153,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(52,211,153,0.08) 1px, transparent 1px)',
              backgroundSize: '40px 40px',
              transform: 'rotateX(62deg)',
              transformOrigin: '50% 0%',
              animation: 'wedgeGridDrift 6s linear infinite',
              maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0) 95%)',
              WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0) 95%)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: '58%',
              height: 1,
              background: 'linear-gradient(90deg, transparent 0%, rgba(52,211,153,0.35) 50%, transparent 100%)',
              boxShadow: '0 0 18px rgba(52,211,153,0.25)',
            }}
          />
        </div>
      </div>
      {/* 2026-08-20 (founder · Fable gateway) · web-only atmosphere: a giant
          radar watermark spanning the page plus amber "$" signal blips in the
          side whitespace. Fixed, behind the card (zIndex 0), pointer-events
          none, aria-hidden, data-wedge-animated so the reduced-motion guard
          freezes it to a full static composition. Never on the native shell. */}
      {!nativeIOS && (
        <div
          data-wedge-animated
          aria-hidden
          style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden' }}
        >
          <div style={{ position: 'absolute', left: '50%', top: '50%', width: '92vmin', height: '92vmin', margin: '-46vmin 0 0 -46vmin', opacity: 0.16 }}>
            <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '2px solid rgba(52,211,153,0.7)', boxShadow: '0 0 46px rgba(52,211,153,0.28), inset 0 0 60px rgba(52,211,153,0.16)' }} />
            <div style={{ position: 'absolute', inset: '3%', borderRadius: '50%', border: '4px dotted rgba(110,231,183,0.4)', animation: 'wedgeSpin 160s linear infinite' }} />
            <div style={{ position: 'absolute', inset: '13%', borderRadius: '50%', border: '1px dashed rgba(110,231,183,0.55)', animation: 'wedgeSpin 90s linear infinite reverse' }} />
            <div style={{ position: 'absolute', inset: '30%', borderRadius: '50%', border: '1px solid rgba(110,231,183,0.4)' }} />
            <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(110,231,183,0.5), transparent)' }} />
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: '50%', width: 1, background: 'linear-gradient(180deg, transparent, rgba(110,231,183,0.5), transparent)' }} />
            <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', inset: 0, background: 'conic-gradient(from 0deg, rgba(110,231,183,0.9) 0deg, rgba(52,211,153,0.4) 10deg, rgba(52,211,153,0.12) 46deg, transparent 64deg)', animation: 'wedgeRadarSweep 9s linear infinite' }} />
            </div>
            <div style={{ position: 'absolute', inset: 0, animation: 'wedgeSpin 24s linear infinite' }}>
              <div style={{ position: 'absolute', left: '76%', top: '50%', width: 11, height: 11, margin: -5.5, borderRadius: '50%', background: '#6EE7B7', boxShadow: '0 0 16px rgba(110,231,183,0.9)' }} />
            </div>
            <div style={{ position: 'absolute', left: '50%', top: '50%', width: 26, height: 26, margin: -13, borderRadius: '50%', background: BRAND.amber, boxShadow: '0 0 36px rgba(245,158,11,0.9), 0 0 80px rgba(245,158,11,0.35)', animation: 'wedgeGlowBreath 2.4s ease-in-out infinite' }} />
          </div>
          {([
            { top: '13%', left: '4.5%', size: 17, delay: 0 },
            { top: '31%', left: '9%', size: 13, delay: 0.7 },
            { top: '67%', left: '5.5%', size: 16, delay: 1.4 },
            { top: '85%', left: '12%', size: 12, delay: 2.1 },
            { top: '16%', right: '4.5%', size: 17, delay: 0.4 },
            { top: '41%', right: '8%', size: 14, delay: 1.1 },
            { top: '70%', right: '6.5%', size: 15, delay: 1.8 },
            { top: '86%', right: '13%', size: 12, delay: 2.5 },
          ] as Array<{ top: string; left?: string; right?: string; size: number; delay: number }>).map((b, i) => (
            <div key={i} className="wedge-blip" style={{ position: 'absolute', top: b.top, left: b.left, right: b.right }}>
              <span style={{ position: 'relative', display: 'inline-block' }}>
                <span style={{ fontFamily: "'Share Tech Mono', ui-monospace, monospace", fontSize: b.size, color: BRAND.amber, textShadow: '0 0 12px rgba(245,158,11,0.7)', display: 'inline-block' }}>$</span>
                <span style={{ position: 'absolute', left: '50%', top: '50%', width: b.size * 3, height: b.size * 3, margin: `${-(b.size * 3) / 2}px 0 0 ${-(b.size * 3) / 2}px`, border: '1px solid rgba(245,158,11,0.4)', borderRadius: '50%', animation: `wedgeBlip ${3.6 + i * 0.2}s cubic-bezier(0.16,1,0.3,1) ${b.delay}s infinite` }} />
              </span>
            </div>
          ))}
        </div>
      )}
      {/* 2026-07-31 · desktop-only quick-link rails fill the wide-screen side
          margins with real navigation (links only, no metrics). Hidden below
          1080px via .wedge-linkrail; the mobile equivalent is the
          .wedge-linkchips row beneath the card.
          2026-08-17 (App Review 3.1.1) · never on the native shell: the CSS
          breakpoint alone is not a gate on iPad landscape (>=1080 CSS px),
          and the Pricing card links to the web checkout page. */}
      {!nativeIOS && (<>
      <nav className="wedge-linkrail wedge-linkrail-left" aria-label="Explore RepVantage">
        <div className="wedge-linkrail-title">Explore</div>
        <a className="wedge-railcard" href="https://repvantage.io" target="_blank" rel="noopener noreferrer">
          <div className="wedge-railcard-label">Visit repvantage.io &rarr;</div>
          <div className="wedge-railcard-sub">The field sales intelligence site</div>
        </a>
        <a className="wedge-railcard" href="https://repvantage.io/prep" target="_blank" rel="noopener noreferrer">
          <div className="wedge-railcard-label">How PREP works &rarr;</div>
          <div className="wedge-railcard-sub">Walk in knowing, explained</div>
        </a>
        <a className="wedge-railcard" href="https://repvantage.io/repxp" target="_blank" rel="noopener noreferrer">
          <div className="wedge-railcard-label">RepXP &rarr;</div>
          <div className="wedge-railcard-sub">Reward your reps</div>
        </a>
        <a className="wedge-railcard" href="https://admin.repvantage.io" target="_blank" rel="noopener noreferrer">
          <div className="wedge-railcard-label">Enterprise &rarr;</div>
          <div className="wedge-railcard-sub">Admin &amp; team console</div>
        </a>
      </nav>
      <nav className="wedge-linkrail wedge-linkrail-right" aria-label="RepVantage resources">
        <div className="wedge-linkrail-title">Resources</div>
        <a className="wedge-railcard wedge-railcard-money" href="https://repvantage.io/pricing" target="_blank" rel="noopener noreferrer">
          <div className="wedge-railcard-label">&larr; Pricing</div>
          <div className="wedge-railcard-sub">See the plans</div>
        </a>
        <a className="wedge-railcard" href="https://shop.repvantage.io" target="_blank" rel="noopener noreferrer">
          <div className="wedge-railcard-label">&larr; Shop</div>
          <div className="wedge-railcard-sub">The RepVantage store</div>
        </a>
        <a className="wedge-railcard" href="https://repvantage.io/support" target="_blank" rel="noopener noreferrer">
          <div className="wedge-railcard-label">&larr; Support</div>
          <div className="wedge-railcard-sub">Get help fast</div>
        </a>
        <a className="wedge-railcard" href="https://repvantage.io/resources" target="_blank" rel="noopener noreferrer">
          <div className="wedge-railcard-label">&larr; Resources</div>
          <div className="wedge-railcard-sub">The enterprise vault</div>
        </a>
      </nav>
      </>)}
      <div
        data-wedge-animated
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 420,
          textAlign: 'center',
        }}
      >
        {view === 'main' ? (
          <>
            <div className="wedge-tagline-incard">{mainHeadline}</div>
            {brandMark}
          </>
        ) : (
          <>
            {brandMark}
            {wordmark}
          </>
        )}

        {view === 'pin' && (
          <div style={{ marginTop: 8 }}>
            <PinLogin
              initialEmail={email}
              onBack={() => setView('main')}
            />
          </div>
        )}

        {/* 2026-07-27 · native-only PIN recovery. Reachable ONLY from the
            iOS shell entry below (setView('recover') is never called on
            web), so the web landing keeps exactly the views it had.

            Copy discipline: this screen says precisely what the emailed
            link does today. The link is https://wedge.repvantage.io/?magic=
            and there is no associated-domains entitlement on the app, so it
            opens the browser and signs the rep in there. It does NOT reset
            a PIN and it does NOT sign them into this app. Saying otherwise
            would be a promise the build cannot keep. */}
        {view === 'recover' && (
          <div style={{ marginTop: 8, textAlign: 'left' }}>
            {recoverState === 'sent' ? (
              <>
                <div style={{ fontSize: 19, fontWeight: 700, textAlign: 'center', marginBottom: 8, letterSpacing: -0.3 }}>
                  Check your email
                </div>
                <div style={{ fontSize: 13.5, color: BRAND.textSec, lineHeight: 1.55, marginBottom: 14 }}>
                  If <span style={{ color: BRAND.text, fontWeight: 600 }}>{recoverEmail.trim()}</span> has a RepVantage account, a sign-in link is on its way. It can take a minute and may land in spam.
                </div>
                <div
                  style={{
                    fontSize: 12.5,
                    color: BRAND.textSec,
                    lineHeight: 1.55,
                    padding: '11px 13px',
                    borderRadius: 10,
                    background: BRAND.surface,
                    border: '1px solid rgba(52,211,153,0.18)',
                    marginBottom: 16,
                  }}
                >
                  Opening that link signs you in at wedge.repvantage.io in your browser. It does not change your PIN and it does not sign you in inside this app.
                </div>
                {/* 2026-07-27 · the primary CTA here used to be "Sign in with
                    my PIN", offered to someone who had just told us they lost
                    it. The primary action is now Back, which returns to the
                    Apple button · the one in-app path that can actually sign
                    this rep in. PIN stays reachable as a subordinate link for
                    the rep who only wanted the link emailed to a colleague or
                    who suddenly remembers it. */}
                <Button appearance="wedge-auth" authSize="native" authSurface="flat" authMotion="instant" authHover="none"
                  type="button"
                  onClick={() => { setRecoverState('idle'); setView('main'); }}
                >
                  Back to sign in
                </Button>
                <Button appearance="wedge-auth" variant="ghost" authSize="native-back" authMotion="instant" authHover="none"
                  type="button"
                  onClick={() => { setView('pin'); }}
                  style={{marginTop: 10}}
                >
                  I remembered my PIN
                </Button>
              </>
            ) : (
              <>
                <div style={{ fontSize: 19, fontWeight: 700, textAlign: 'center', marginBottom: 6, letterSpacing: -0.3 }}>
                  Email me a sign-in link
                </div>
                <div style={{ fontSize: 13, color: BRAND.textSec, textAlign: 'center', lineHeight: 1.5, marginBottom: 14 }}>
                  Enter the email you use for RepVantage.
                </div>
                {/* 2026-07-27 · this disclosure used to appear only AFTER the
                    rep submitted, which meant they opted into a flow before
                    learning it lands them in the browser rather than in this
                    app. It belongs above the input, where it can still change
                    the decision. The Apple line below it is the path that
                    actually gets them back INTO the app: findOrCreateOAuthUser
                    looks the user up by email before creating one, so an Apple
                    ID whose email matches their RepVantage email signs them
                    into their existing account. Hedged on purpose · a Private
                    Relay address or a different Apple ID email will not
                    match. */}
                <div
                  style={{
                    fontSize: 12.5,
                    color: BRAND.textSec,
                    lineHeight: 1.55,
                    padding: '11px 13px',
                    borderRadius: 10,
                    background: BRAND.surface,
                    border: '1px solid rgba(52,211,153,0.18)',
                    marginBottom: 14,
                  }}
                >
                  The link opens wedge.repvantage.io in your browser and signs you in there. It does not change your PIN and it does not sign you in inside this app.
                  <br />
                  <br />
                  <span style={{ color: BRAND.text, fontWeight: 600 }}>Faster, if it fits:</span> if you signed up with this same email, go back and tap Continue with Apple. It puts you straight into your existing account here, with no PIN and no email.
                </div>
                <TextField variant="wedge-auth" authSize="recovery" authMotion="instant"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="off"
                  spellCheck={false}
                  enterKeyHint="send"
                  aria-label="Email for your sign-in link"
                  value={recoverEmail}
                  onChange={(ev) => { setRecoverEmail(ev.target.value); if (recoverError) setRecoverError(''); }}
                  placeholder="you@company.com"
                  disabled={recoverState === 'sending'}
                />
                {recoverError && (
                  <Alert variant="wedge-auth" authLook="form-error" role="alert" style={{ margin: '2px 0 12px' }}>
                    {recoverError}
                  </Alert>
                )}
                <Button appearance="wedge-auth" authSize="native" authSurface="flat" authMotion="instant" authHover="none"
                  type="button"
                  onClick={() => { void submitRecovery(); }}
                  disabled={recoverState === 'sending'}
                  aria-busy={recoverState === 'sending'}
                >
                  {recoverState === 'sending' ? 'Sending link...' : 'Email me a sign-in link'}
                </Button>
                <Button appearance="wedge-auth" variant="ghost" authSize="native-back" authMotion="instant" authHover="none"
                  type="button"
                  onClick={() => { setRecoverError(''); setView('main'); }}
                  disabled={recoverState === 'sending'}
                  style={{marginTop: 10}}
                >
                  Back
                </Button>
              </>
            )}
          </div>
        )}

        {view === 'main' && (
        <>
        <div
          data-wedge-animated
          style={nativeIOS ? {
            fontSize: 13.5,
            color: BRAND.textSec,
            maxWidth: 320,
            margin: '12px auto 18px',
            lineHeight: 1.5,
            animation: 'wedgeStagger 520ms cubic-bezier(0.16, 1, 0.3, 1) 620ms backwards',
          } : {
            fontFamily: BRAND.fontDisplay,
            fontWeight: 800,
            fontSize: 'clamp(1.15rem, 3.6vw, 1.7rem)',
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            transform: 'skewX(-6deg)',
            color: '#F0F0F0',
            maxWidth: 380,
            margin: '10px auto 18px',
            lineHeight: 1.12,
            textShadow: '0 0 26px rgba(52,211,153,0.3)',
            animation: 'wedgeStagger 520ms cubic-bezier(0.16, 1, 0.3, 1) 620ms backwards',
          }}
        >
          {nativeIOS ? (
            'Know the account before you walk in the door.'
          ) : (
            <>Built by a rep, <span style={{ color: '#34D399', textShadow: '0 0 26px rgba(52,211,153,0.55)' }}>for the reps</span></>
          )}
        </div>

        {topError && (
          <Alert variant="wedge-auth" authLook="compact-warning" style={{marginBottom:16}}>
            {topError}
          </Alert>
        )}

        {/* V3 · aria-live region · invisible visually, read by screen readers
            when magicState changes. role=status + aria-live=polite is the
            spec-recommended combination for transient form-feedback. */}
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0,0,0,0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {liveMessage}
        </div>

        {/* W4 · offline banner */}
        {!isOnline && (
          <Alert variant="wedge-auth" authLook="compact-warning" style={{marginBottom:12}}>
            You appear to be offline. We will retry the moment you reconnect.
          </Alert>
        )}

        {/* W8 · cookie/storage warning · third-party cookies blocked */}
        {!nativeIOS && cookieWarning && (
          <div
            style={{
              fontSize: 12.5,
              padding: '10px 12px',
              borderRadius: 10,
              background: 'rgba(245,158,11,0.10)',
              border: '1px solid rgba(245,158,11,0.30)',
              color: BRAND.warningControlInk,
              marginBottom: 12,
              lineHeight: 1.45,
              textAlign: 'left',
            }}
          >
            Third-party cookies look blocked in this browser. Continue with Google may not stick · the sign-in-link path will.
          </div>
        )}

        {/* V7 · login-loop guard banner · only shows after 3+ magic-link
            sends in 10 min, steers the rep to Google as the path that will
            definitely work for them. Web only · it names a Google button
            that the native shell does not render. */}
        {!nativeIOS && loopBannerVisible && magicState !== 'sent' && (
          <div
            style={{
              fontSize: 12.5,
              padding: '10px 12px',
              borderRadius: 10,
              background: 'rgba(148,163,184,0.10)',
              border: '1px solid rgba(148,163,184,0.28)',
              color: '#CBD5E1',
              marginBottom: 16,
              lineHeight: 1.45,
              textAlign: 'left',
            }}
          >
            Sign-in link not arriving? Continue with Google above gets you in instantly.
          </div>
        )}

        {/* 2026-07-13 · in-app webview banner · shown proactively so the
            rep is steered out BEFORE tapping into Google's 403 wall. */}
        {inAppInfo.inApp && (
          <Alert variant="wedge-auth" authLook="in-app" style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              {inAppInfo.app
                ? `You're inside ${inAppInfo.app}'s built-in browser.`
                : "You're inside an app's built-in browser."}
            </div>
            Google blocks sign-in here for security. Open this page in{' '}
            {inAppInfo.platform === 'android' ? 'Chrome' : 'Safari'} to continue with
            Google, {nativeIOS
              ? <>or sign in with your <b>email and password</b> below · that works right here.</>
              : <>or use <b>Email me a sign-in link</b> below · that works right here.</>}
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <Button appearance="wedge-notice" noticeLook="in-app-open"
                type="button"
                onClick={escapeInAppBrowser}
                style={{ flex: 1 }}
              >
                Open in {inAppInfo.platform === 'android' ? 'Chrome' : 'Safari'}
              </Button>
              <Button appearance="wedge-notice" noticeLook="in-app-copy"
                type="button"
                onClick={copyLandingLink}
                style={{ flex: 1 }}
              >
                {inAppLinkCopied ? 'Link copied' : 'Copy link'}
              </Button>
            </div>
            {inAppEscapeTried && (
              <div style={{ marginTop: 8, opacity: 0.85 }}>
                Nothing happened? Tap the <b>&hellip;</b> or share menu in the corner and
                choose <b>Open in Browser</b>, or paste the copied link into{' '}
                {inAppInfo.platform === 'android' ? 'Chrome' : 'Safari'}.
              </div>
            )}
          </Alert>
        )}

        {/* NATIVE Apple sign-in · iOS shell only (App Review 4.8). 2026-07-27:
            this is the PRIMARY and default native path · Google cannot run in
            an embedded webview and the inline magic-link form strands the rep
            in the system browser, so neither renders here. A subordinate PIN
            entry for returning reps sits below. Black primary button per
            Apple's expected look. */}
        {/* 2026-08-26 · Bug 9A · SIWA orphan recovery panel (native iOS only).
            When the on-device Apple grant is a known orphan (apple_email_hidden
            401 this session, or remembered across a re-onboard), lead with the
            paths that actually get the rep in on attempt 1 · native Google (when
            the plugin is in the build), the magic sign-in link, and PIN · so the
            screen no longer re-presents the same failing Apple button as the
            primary. Handlers are surfaced as visible options, never auto-invoked.
            The full reset-in-Settings instruction still renders in the Apple
            error box below. */}
        {nativeIOS && appleGrantOrphan && (
          <div
            role="group"
            aria-label="Other ways to sign in"
            style={{
              padding: '14px 14px',
              borderRadius: 12,
              background: BRAND.surface,
              border: '1px solid rgba(52,211,153,0.20)',
              textAlign: 'left',
              marginBottom: 12,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 700, color: BRAND.text, marginBottom: 6 }}>
              Get in another way
            </div>
            <div style={{ fontSize: 12.5, color: BRAND.textSec, lineHeight: 1.5, marginBottom: 12 }}>
              Apple is not sharing your email, so it cannot finish here. Use one of these to sign in now, or reset the Apple grant in Settings (steps below) and try Apple again.
            </div>
            {googleNativeReady && (
              <Button appearance="wedge-signin" signInLook="alternate"
                type="button"
                onClick={() => { void startGoogleSignInNative(); }}
                disabled={googleNativeState === 'authorizing'}
                style={{ marginBottom: 8 }}
              >
                {googleNativeState === 'authorizing' ? 'Signing in...' : 'Continue with Google'}
              </Button>
            )}
            <Button appearance="wedge-signin" signInLook="alternate"
              type="button"
              onClick={() => { setView('recover'); }}
              style={{ marginBottom: 8 }}
            >
              Email me a sign-in link
            </Button>
            <Button appearance="wedge-signin" signInLook="alternate"
              type="button"
              onClick={() => { setView('pin'); }}
              style={{ marginBottom: 0 }}
            >
              Sign in with your PIN
            </Button>
          </div>
        )}
        {nativeIOS && appleError && (
          <div
            role="alert"
            style={{
              padding: '10px 14px',
              borderRadius: 12,
              background: 'rgba(248,113,113,0.10)',
              border: '1px solid rgba(248,113,113,0.35)',
              color: BRAND.softErrorInk,
              fontSize: 12.5,
              lineHeight: 1.45,
              textAlign: 'left',
              margin: '0 0 10px',
            }}
          >
            {appleError}
          </div>
        )}
        {nativeIOS && (
          <Button appearance="wedge-signin" signInLook="native-apple" signInPending={appleState === 'authorizing'}
            onClick={() => { void startAppleSignInNative(); }}
            disabled={appleState === 'authorizing'}
            aria-busy={appleState === 'authorizing'}
            aria-label={lastMethod === 'apple' ? 'Continue with Apple · last used' : 'Continue with Apple'}
            style={{marginBottom: 10}}
          >
            {appleState === 'authorizing' ? (
              <>
                <span className="wedge-spinner-light" aria-hidden="true" />
                <span>Signing in&hellip;</span>
              </>
            ) : (
              <>
                <svg width={18} height={18} viewBox="0 0 384 512" aria-hidden="true">
                  <path fill="#fff" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                </svg>
                <span>Continue with Apple</span>
                {lastMethod === 'apple' && (
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: 0.8,
                      padding: '2px 6px',
                      borderRadius: 5,
                      background: BRAND.border,
                      color: BRAND.accent,
                      border: '1px solid rgba(52,211,153,0.40)',
                    }}
                  >
                    LAST USED
                  </span>
                )}
              </>
            )}
          </Button>
        )}
        {/* NATIVE Google · iOS only, and only once the GoogleAuth plugin is in
            the build (googleNativeReady) so it never renders as a dead control.
            Sits BELOW Continue with Apple so Apple stays at least as prominent
            (App Store 4.8). Links to the same account by email on the backend. */}
        {nativeIOS && googleNativeReady && (
          <Button appearance="wedge-signin" signInLook="native-light" signInPending={googleNativeState === 'authorizing'}
            onClick={() => { void startGoogleSignInNative(); }}
            disabled={googleNativeState === 'authorizing'}
            aria-busy={googleNativeState === 'authorizing'}
            aria-label={lastMethod === 'google' ? 'Continue with Google · last used' : 'Continue with Google'}
            style={{marginBottom: 10}}
          >
            {googleNativeState === 'authorizing' ? (
              <span>Signing in&hellip;</span>
            ) : (
              <>
                <svg width={18} height={18} viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
                  <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
                  <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"/>
                  <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
                </svg>
                <span>Continue with Google</span>
                {lastMethod === 'google' && (
                  <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.8, padding: '2px 6px', borderRadius: 5, background: BRAND.border, color: '#016241', border: '1px solid rgba(52,211,153,0.40)' }}>LAST USED</span>
                )}
              </>
            )}
          </Button>
        )}
        {nativeIOS && googleNativeError && (
          <div role="alert" style={{ marginTop: -2, marginBottom: 10, fontSize: 12.5, color: BRAND.redText, textAlign: 'center' }}>{googleNativeError}</div>
        )}
        {/* NATIVE Microsoft · iOS only, and only once the Browser plugin is in
            the build (microsoftNativeReady) so it never renders as a dead
            control. Sits BELOW Continue with Apple so Apple stays at least as
            prominent (App Store 4.8). startMicrosoftSignIn auto-routes to the
            installed-app PKCE flow on native and links the same account by email
            on the backend. Gated on microsoftNativeAvailable(), NOT the web
            /api/auth/providers list, since the native flow is client-side PKCE. */}
        {nativeIOS && microsoftNativeReady && (
          <Button appearance="wedge-signin" signInLook="native-light" signInPending={msState !== 'idle'}
            onClick={() => { void startMicrosoftSignIn(); }}
            disabled={msState !== 'idle'}
            aria-busy={msState !== 'idle'}
            aria-label={lastMethod === 'microsoft' ? 'Continue with Microsoft · last used' : 'Continue with Microsoft'}
            style={{marginBottom: 10}}
          >
            {msState !== 'idle' ? (
              <span>Signing in&hellip;</span>
            ) : (
              <>
                <svg width={18} height={18} viewBox="0 0 23 23" aria-hidden="true">
                  <path fill="#F25022" d="M1 1h10v10H1z"/>
                  <path fill="#7FBA00" d="M12 1h10v10H12z"/>
                  <path fill="#00A4EF" d="M1 12h10v10H1z"/>
                  <path fill="#FFB900" d="M12 12h10v10H12z"/>
                </svg>
                <span>Continue with Microsoft</span>
                {lastMethod === 'microsoft' && (
                  <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.8, padding: '2px 6px', borderRadius: 5, background: BRAND.border, color: '#016241', border: '1px solid rgba(52,211,153,0.40)' }}>LAST USED</span>
                )}
              </>
            )}
          </Button>
        )}

        {/* 2026-08-19 · email + password (Log in / Sign up) sits below the
            native Apple/Google buttons on the iOS shell (founder 2026-08-19).
            2026-09-27 (Luke · Google-only web copy) · NATIVE ONLY now. Web
            follows the standing rule "Google OAuth only in wedge copy (never
            mention codes/passwords)" and offers Google plus the one-tap
            sign-in link further down instead. The iOS render is unchanged
            (App Review signs in on this surface). Backend routes stay live. */}
        {nativeIOS && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '2px 0 16px' }}>
          <span style={{ flex: 1, height: 1, background: 'rgba(52,211,153,0.16)' }} />
          <span style={{ fontSize: 12, fontWeight: 400, letterSpacing: 2, textTransform: 'uppercase', color: '#8494A8', fontFamily: "'Share Tech Mono', ui-monospace, monospace" }}>or</span>
          <span style={{ flex: 1, height: 1, background: 'rgba(52,211,153,0.16)' }} />
        </div>
        )}

        {nativeIOS && (<>
        <form
          onSubmit={(e) => { e.preventDefault(); setEmailTouched(true); if (pwState !== 'submitting') void submitPassword(); }}
          style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
        >
          {authMode === 'signup' && (
            <TextField variant="wedge-auth-account"
              type="text" name="name" autoComplete="name" placeholder="Your name (optional)"
              value={pwName} onChange={(e) => setPwName(e.target.value)}
            />
          )}
          <TextField variant="wedge-auth-account" authBorder={emailTouched && email && !emailLooksValid ? 'warning' : undefined}
            type="email" name="email" inputMode="email" autoComplete="email" placeholder="you@company.com"
            value={email} onChange={(e) => setEmail(e.target.value)} onBlur={() => setEmailTouched(true)}
            aria-label="Email"
          />
          <div style={{ position: 'relative' }}>
            <TextField variant="wedge-auth-account" authInset="password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete={authMode === 'signup' ? 'new-password' : 'current-password'}
              placeholder={authMode === 'signup' ? 'Create a password' : 'Password'}
              value={password} onChange={(e) => setPassword(e.target.value)}
              aria-label="Password"
            />
            <Button appearance="wedge-account" accountLook="password-toggle" type="button" onClick={() => setShowPassword((s) => !s)} aria-label={showPassword ? 'Hide password' : 'Show password'}
              style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }}>
              {showPassword ? 'Hide' : 'Show'}
            </Button>
          </div>
          {pwError && (
            <div role="alert" style={{ fontSize: 12.5, color: BRAND.passwordErrorInk, lineHeight: 1.45 }}>{pwError}</div>
          )}
          {pwNotice && (
            <div style={{ fontSize: 12.5, color: BRAND.accent, lineHeight: 1.45 }}>{pwNotice}</div>
          )}
          <Button appearance="wedge-account" accountLook="solid" accountPending={pwState === 'submitting'} type="submit" disabled={pwState === 'submitting'}>
            {pwState === 'submitting' && (
              <span aria-hidden style={{ position: 'absolute', inset: 0, background: 'linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.35) 50%, transparent 65%)', animation: 'wedgeShimmer 1.1s linear infinite' }} />
            )}
            <span style={{ position: 'relative' }}>{pwState === 'submitting' ? 'Authenticating...' : authMode === 'signup' ? 'Create account' : 'Log in'}</span>
          </Button>
        </form>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, fontSize: 12.5 }}>
          <Button appearance="wedge-account" accountLook={authMode === 'login' ? 'signup-invite' : 'login-link'} type="button"
            onClick={() => { setAuthMode((m) => (m === 'login' ? 'signup' : 'login')); setPwError(''); setPwNotice(''); }}>
            {authMode === 'login' ? 'New here? Sign up' : 'Have an account? Log in'}
          </Button>
          {authMode === 'login' && (
            <Button appearance="wedge-account" accountLook="forgot" accountPending={forgotState === 'sending'} type="button" disabled={forgotState === 'sending'} onClick={() => void submitForgot()}>
              {forgotState === 'sent' ? 'Reset link sent' : 'Forgot password?'}
            </Button>
          )}
        </div>
        </>)}

        {/* PRIMARY · Continue with Google. Hidden inside the iOS shell:
            Google blocks OAuth in embedded webviews (disallowed_useragent),
            so a visible Google button there is a dead control · Apple
            sign-in above is the native path and links to the same account
            by email. The web treatment is unchanged. */}
        {!nativeIOS && (<>
        {/* PRIMARY · Continue with Google */}
        <Button appearance="wedge-signin" signInLook="web-google" signInPending={googleState === 'redirecting'}
            onClick={startGoogleSignIn}
            disabled={googleState === 'redirecting'}
            aria-busy={googleState === 'redirecting'}
            aria-label={lastMethod === 'google' ? 'Continue with Google · last used' : 'Continue with Google'}
          >
          {googleState === 'redirecting' ? (
            <>
              <span className="wedge-spinner" aria-hidden="true" />
              <span>Redirecting&hellip;</span>
            </>
          ) : (
            <>
              <svg width={18} height={18} viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              <span>Continue with Google</span>
              {/* W10 · last-used method memory · a small "Last used" chip
                  marks the path the rep successfully used last time. */}
              {lastMethod === 'google' && (
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: 0.8,
                    padding: '2px 6px',
                    borderRadius: 5,
                    background: BRAND.border,
                    color: '#016241',
                    border: '1px solid rgba(52,211,153,0.40)',
                  }}
                >
                  LAST USED
                </span>
              )}
            </>
          )}
        </Button>

        {/* U8 · Microsoft button · only when provider list includes it */}
        {providers.includes('microsoft') && (
          <Button appearance="wedge-signin" signInLook="web-microsoft" signInPending={msState === 'redirecting'}
            onClick={() => { void startMicrosoftSignIn(); }}
            disabled={msState === 'redirecting'}
            aria-busy={msState === 'redirecting'}
            style={{marginTop: 10}}
          >
            {msState === 'redirecting' ? (
              <>
                <span className="wedge-spinner-light" aria-hidden="true" />
                <span>Redirecting&hellip;</span>
              </>
            ) : (
              <>
                <svg width={16} height={16} viewBox="0 0 23 23" aria-hidden="true">
                  <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
                  <rect x="12" y="1" width="10" height="10" fill="#7FBA00"/>
                  <rect x="1" y="12" width="10" height="10" fill="#00A4EF"/>
                  <rect x="12" y="12" width="10" height="10" fill="#FFB900"/>
                </svg>
                <span>Continue with Microsoft</span>
              </>
            )}
          </Button>
        )}
        {/* Sign in with Apple · App Store Guideline 4.8 · rendered when the
            backend publishes 'apple' (Apple dev creds configured). */}
        {providers.includes('apple') && (
          <Button appearance="wedge-signin" signInLook="web-apple" signInPending={appleState === 'redirecting'}
            onClick={startAppleSignIn}
            disabled={appleState === 'redirecting'}
            aria-busy={appleState === 'redirecting'}
            aria-label={lastMethod === 'apple' ? 'Continue with Apple · last used' : 'Continue with Apple'}
            style={{marginTop: 10}}
          >
            {appleState === 'redirecting' ? (
              <>
                <span className="wedge-spinner-light" aria-hidden="true" />
                <span>Redirecting&hellip;</span>
              </>
            ) : (
              <>
                <svg width={16} height={16} viewBox="0 0 384 512" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"
                  />
                </svg>
                <span>Continue with Apple</span>
              </>
            )}
          </Button>
        )}
        </>)}
        {/* 2026-07-27 · this caption belongs to the Google/magic-link cluster
            above and was left ungated when that cluster went web-only, so on
            native it stranded "One tap, no code, no waiting." between the
            Apple button and the PIN entry, reading as a caption for controls
            it does not describe. Gated to web. */}
        {!nativeIOS && (
        <div style={{ fontSize: 12, color: BRAND.textSec, marginTop: 8, marginBottom: 18 }}>
          One tap, no waiting.
        </div>
        )}

        {/* 2026-07-27 · NATIVE secondary + recovery, deliberately subordinate
            to the Apple button above (link weight, no fill, smaller type) so
            a new rep reads the screen as "tap the black button".

            Wording: the destination screen is titled "Sign in with your PIN",
            the PIN flow calls it a PIN, and the stored credential is 4 to 12
            letters or numbers. Calling it a password on the way in and a PIN
            on arrival is the mismatch that makes a returning rep bail, so the
            entry says PIN.

            The recovery entry is the honest half of the flow that exists
            today: the sign-in-link email is real and wired, but the link is a
            web URL and the app has no associated-domains entitlement, so the
            recover screen tells the rep exactly where the link lands. */}



        {/* 2026-07-31 · YC reviewer sign-in button removed from the landing
            (Luke's call). The ?yc=CODE deep-link and the 'yc' redeem view
            still work via WedgeAuthGate's CodeAutoRedeem path, so a reviewer
            with the link keeps access; there is just no public button here. */}

        {/* 2026-09-27 (Luke · Google-only web copy) · WEB no-Google fallback.
            The email + password form is gone from web; reps without a Google
            account (including anyone who signed up with email + password) get
            in with the one-tap sign-in link, restored from the pre-2026-08-19
            round-17 treatment and wired to the existing sendMagicLink
            (POST /api/auth/wedge-access/email-me · always 201, no email
            enumeration). The pre-approved-domain access-code panel was NOT
            restored. Native iOS never renders this: the emailed link opens the
            system browser and strands the rep outside the app. */}
        {!nativeIOS && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 16px' }}>
          <span style={{ flex: 1, height: 1, background: 'rgba(52,211,153,0.16)' }} />
          <span style={{ fontSize: 12, fontWeight: 400, letterSpacing: 2, textTransform: 'uppercase', color: '#8494A8', fontFamily: "'Share Tech Mono', ui-monospace, monospace" }}>or</span>
          <span style={{ flex: 1, height: 1, background: 'rgba(52,211,153,0.16)' }} />
        </div>
        )}

        {!nativeIOS && magicState !== 'sent' && (
          <form
            onSubmit={(e) => { e.preventDefault(); setEmailTouched(true); void sendMagicLink(); }}
            style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
          >
            <div style={{ position: 'relative' }}>
              <TextField variant="wedge-auth-magic" authBorder={emailTouched && email && !emailLooksValid ? 'warning' : undefined}
                ref={emailInputRef}
                type="email"
                inputMode="email"
                autoComplete="email"
                name="email"
                id="wedge-magic-email"
                aria-label="Email for your sign-in link"
                spellCheck={false}
                enterKeyHint="send"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (magicState === 'error') { setMagicState('idle'); setMagicError(''); setMagicErrorBoth(false); }
                }}
                /* W2 · paste-detect immediate validation feedback */
                onPaste={() => setEmailTouched(true)}
                onBlur={() => { setEmailTouched(true); normalizeEmailNow(); }}
                placeholder="you@company.com"
                disabled={magicState === 'sending'}
              />
              {/* U5 · inline valid green check (only when valid) */}
              {emailLooksValid && (
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    right: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 18,
                    height: 18,
                    borderRadius: 999,
                    background: 'rgba(52,211,153,0.20)',
                    border: '1px solid rgba(52,211,153,0.42)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#34D399',
                    fontSize: 12,
                    fontWeight: 800,
                  }}
                >
                  &#10003;
                </span>
              )}
            </div>

            {/* W1 · typo correction · "Did you mean ...?" tap to accept */}
            {typoSuggestion && magicState !== 'sending' && (
              <div
                style={{
                  marginTop: -4,
                  padding: '4px 4px 4px 12px',
                  borderRadius: 10,
                  background: 'rgba(245,158,11,0.10)',
                  border: '1px solid rgba(245,158,11,0.36)',
                  fontSize: 12,
                  color: BRAND.warningControlInk,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  textAlign: 'left',
                }}
              >
                <span>Did you mean <strong style={{ color: '#fff' }}>{typoSuggestion}</strong>?</span>
                <Button appearance="wedge-email" email={{ look: "typo" }}
                  type="button"
                  onClick={() => { setEmail(typoSuggestion); setEmailTouched(true); }}
                  style={{ flexShrink: 0 }}
                >
                  Fix it
                </Button>
              </div>
            )}

            {/* U2 · domain typeahead suggestions */}
            {typeaheadSuggestions.length > 0 && magicState !== 'sending' && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: -4 }}>
                {typeaheadSuggestions.map((s) => (
                  <Button appearance="wedge-email" email={{ look: "domain" }}
                    key={s}
                    type="button"
                    onClick={() => acceptSuggestion(s)}
                  >
                    {s}
                  </Button>
                ))}
              </div>
            )}

            <Button appearance="wedge-email" email={{ look: "send", state: magicState === "sending" ? "sending" : undefined }}
              type="submit"
              disabled={magicState === 'sending'}
              aria-busy={magicState === 'sending'}
            >
              {magicState === 'sending' && <span className="wedge-spinner-light" aria-hidden="true" />}
              <span>{magicState === 'sending' ? 'Sending...' : 'Email me a sign-in link'}</span>
            </Button>

            {/* U5 · soft hint when email format is invalid and the user has interacted */}
            {emailTouched && email && !emailLooksValid && (
              <div style={{ fontSize: 12, color: BRAND.warningControlInk, lineHeight: 1.4, textAlign: 'left' }}>
                That doesn&rsquo;t look like a complete email address yet.
              </div>
            )}
            {magicError && (
              <div role="alert" style={{ fontSize: 12.5, color: BRAND.softErrorInk, lineHeight: 1.45, textAlign: 'left', marginTop: 2 }}>
                {magicError}
              </div>
            )}
            {/* U10 · friendly both-providers-down state · one-tap Google fallback */}
            {magicErrorBoth && (
              <Button appearance="wedge-signin" signInLook="fallback-google"
                type="button"
                onClick={startGoogleSignIn}
                style={{ marginTop: 2 }}
              >
                <svg width={16} height={16} viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                </svg>
                Use Google instead
              </Button>
            )}
            <div style={{ fontSize: 12, color: BRAND.textSec, marginTop: 4, lineHeight: 1.5 }}>
              No Google account? We email you a one-tap link that signs you in.
            </div>
          </form>
        )}

        {!nativeIOS && magicState === 'sent' && (
          <div
            data-wedge-animated
            style={{
              padding: '20px 18px',
              borderRadius: 16,
              background: BRAND.surfaceStrong,
              border: '1px solid rgba(52,211,153,0.36)',
              color: '#E6F4EE',
              animation: 'wedgeFadeUp 360ms cubic-bezier(0.16, 1, 0.3, 1)',
              textAlign: 'left',
            }}
          >
            <div style={{ fontSize: 14.5, fontWeight: 800, color: '#34D399', marginBottom: 6 }}>
              Check your inbox
            </div>
            {/* Neutral on purpose: the backend answers ok for every
                well-formed address, so this copy never says whether an
                account exists. The link is single-use with a 15-minute life
                (MAGIC_TTL_MS), so the old "stays active" promise is gone. */}
            <div style={{ fontSize: 13, color: '#CBD5E1', lineHeight: 1.5 }}>
              A sign-in link is on its way to <strong style={{ color: '#fff' }}>{email}</strong>. It can take a minute and may land in spam. Open it on this device; it works once and expires in 15 minutes.
            </div>
            {/* W9 · PWA install nudge for iOS Safari non-standalone users. */}
            {(() => {
              try {
                const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
                const isIOS = /iPhone|iPad|iPod/i.test(ua);
                const isSafari = isIOS && /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
                const standalone = (typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches)
                  || (typeof navigator !== 'undefined' && (navigator as Navigator & { standalone?: boolean }).standalone === true);
                if (!(isSafari && !standalone)) return null;
                return (
                  <div
                    style={{
                      marginTop: 12,
                      padding: '10px 12px',
                      borderRadius: 10,
                      background: 'rgba(245,158,11,0.08)',
                      border: '1px dashed rgba(245,158,11,0.32)',
                      fontSize: 12,
                      color: '#FFDBA8',
                      lineHeight: 1.45,
                    }}
                  >
                    After signing in: Share &rarr; Add to Home Screen unlocks push alerts for ready briefs.
                  </div>
                );
              } catch { return null; }
            })()}

            {/* V4 · open-mail-app deep links · the rep's domain match comes
                first, then the other big ones. */}
            {(() => {
              const at = email.indexOf('@');
              const domain = at > 0 ? email.slice(at + 1).toLowerCase() : '';
              const matching = MAIL_APPS.filter((m) => m.matchDomains?.includes(domain));
              const others = MAIL_APPS.filter((m) => !matching.includes(m));
              const ordered = [...matching, ...others].slice(0, matching.length > 0 ? 4 : 3);
              return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
                  {ordered.map((app, idx) => (
                    <a
                      key={app.label}
                      href={app.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: '0 14px',
                        borderRadius: 999,
                        background: idx === 0 && matching.length > 0 ? 'rgba(52,211,153,0.20)' : BRAND.surface,
                        border: `1px solid ${idx === 0 && matching.length > 0 ? 'rgba(52,211,153,0.46)' : BRAND.border}`,
                        color: idx === 0 && matching.length > 0 ? '#A0F0CC' : BRAND.text,
                        fontSize: 12,
                        fontWeight: 700,
                        textDecoration: 'none',
                        fontFamily: 'inherit',
                        minHeight: 44,
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      {app.label} &rarr;
                    </a>
                  ))}
                </div>
              );
            })()}
            {/* U3 · resend cooldown · button enables when counter reaches 0 */}
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
              <Button appearance="wedge-email" email={{ look: "resend", state: cooldown === 0 ? undefined : "cooldown" }}
                type="button"
                onClick={() => { if (cooldown === 0 && magicState === 'sent') void sendMagicLink(); }}
                disabled={cooldown > 0}
              >
                {cooldown === 0 ? 'Resend link' : `Resend in ${cooldown}s`}
              </Button>
              <Button appearance="wedge-email" email={{ look: "change-address" }}
                type="button"
                onClick={() => {
                  setMagicState('idle');
                  setMagicError('');
                  setMagicErrorBoth(false);
                  setEmail('');
                  setEmailTouched(false);
                  setCooldown(0);
                  try { localStorage.removeItem(LAST_EMAIL_KEY); } catch { /* ignore */ }
                }}
              >
                Send to a different email
              </Button>
            </div>
          </div>
        )}
        </>
        )}
      </div>

      {/* W7 · privacy + terms · trust signal + basic compliance · small,
          non-intrusive, beneath the main affordance. 2026-06-10 · tap
          targets bumped to 44pt HIG via padding · prior 14pt height was
          a thumb-miss on phone.
          2026-08-01 (b56) · native iOS only · this row was too dim (0.6 opacity
          on #64748B) and floated a big gap below "Lost your PIN?". Brighten it
          (full opacity + #94A3B8, matching the PIN link above) and pull it up to
          marginTop:0 so it reads as the even third line of the stack. Web keeps
          its marginTop:20 placement.
          2026-09-26 (design wave) · web now shares the native ink: the dim
          0.6 x #64748B pair measured 2.1:1 on the canvas (AA needs 4.5), and
          the links grow to a 44px tap target. */}
      <div
        className="wedge-signin-footer"
        style={{
          marginTop: nativeIOS ? 0 : 20,
          fontSize: 12,
          opacity: 1,
          textAlign: 'center',
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <a
          href="https://repvantage.io/privacy"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: BRAND.textSec,
            textDecoration: 'none',
            padding: '6px 12px',
            minHeight: 44,
            display: 'inline-flex',
            alignItems: 'center',
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation',
          }}
        >
          Privacy
        </a>
        <span style={{ opacity: 0.4 }} aria-hidden="true">&middot;</span>
        <a
          href="https://repvantage.io/terms"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: BRAND.textSec,
            textDecoration: 'none',
            padding: '6px 12px',
            minHeight: 44,
            display: 'inline-flex',
            alignItems: 'center',
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation',
          }}
        >
          Terms
        </a>
      </div>

      {/* 2026-07-31 · mobile/tablet quick links (real links only). Hidden at
          >=1080px where the side rails take over. */}
      {/* 2026-08-01 (b56) · same links, now data-driven so the native iOS shell
          can drop Platform + Support and keep the phone row tight to the six that
          matter. Web/desktop render the full set unchanged (filter is a no-op
          when nativeIOS is false). */}
      <nav className="wedge-linkchips" aria-label="RepVantage links">
        {[
          { href: 'https://repvantage.io', label: 'Website' },
          { href: 'https://admin.repvantage.io', label: 'Enterprise' },
          { href: 'https://shop.repvantage.io', label: 'Shop' },
        ]
          .map((l) => (
            <a key={l.label} className="wedge-linkchip" href={l.href} target="_blank" rel="noopener noreferrer">{l.label}</a>
          ))}
      </nav>
      <div
        className="wedge-domain-line"
        style={{
          marginTop: 8,
          fontSize: 12,
          color: '#8494A8',
          letterSpacing: 1,
          textAlign: 'center',
          fontFamily: "'Share Tech Mono', ui-monospace, monospace",
        }}
      >
        wedge.repvantage.io &middot; field sales intelligence
      </div>

      {/* 2026-08-20 (founder · Fable gateway) · desktop-web status bar. Fixed
          to the bottom, revealed only >=720px on non-native by .wedge-statusbar.
          Real outbound social links; never renders on the native shell. */}
      {!nativeIOS && (
        <div className="wedge-statusbar" aria-label="RepVantage status">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            SECURE CHANNEL &middot; AES-256
            <span aria-hidden="true" style={{ opacity: 0.4 }}>&middot;</span>
            <a href="https://repvantage.io/privacy" target="_blank" rel="noopener noreferrer" style={{ letterSpacing: '1.4px' }}>PRIVACY</a>
            <span aria-hidden="true" style={{ opacity: 0.4 }}>&middot;</span>
            <a href="https://repvantage.io/terms" target="_blank" rel="noopener noreferrer" style={{ letterSpacing: '1.4px' }}>TERMS</a>
          </span>
          <span className="wedge-status-mid" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#6EE7B7' }}>
            <span style={{ width: 6, height: 6, borderRadius: 999, background: '#34D399', boxShadow: '0 0 8px rgba(52,211,153,0.9)', animation: 'wedgeLiveDot 1.8s ease-in-out infinite' }} />
            ALL SIGNALS NOMINAL
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 16 }}>
            <a href="https://www.youtube.com/@repvantage" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31.3 31.3 0 0 0 0 12a31.3 31.3 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31.3 31.3 0 0 0 24 12a31.3 31.3 0 0 0-.5-5.8zM9.5 15.6V8.4L15.8 12l-6.3 3.6z"/></svg>
            </a>
            <a href="https://www.linkedin.com/company/repvantage" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.4 20.4h-3.6v-5.6c0-1.3 0-3-1.9-3s-2.1 1.4-2.1 2.9v5.7H9.2V9h3.4v1.6h.1a3.8 3.8 0 0 1 3.4-1.9c3.6 0 4.3 2.4 4.3 5.5v6.2zM5.2 7.4a2.1 2.1 0 1 1 0-4.2 2.1 2.1 0 0 1 0 4.2zM7 20.4H3.4V9H7v11.4z"/></svg>
            </a>
            <a href="https://www.instagram.com/repvantage" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4.5"/><circle cx="17.8" cy="6.2" r="1.4" fill="currentColor" stroke="none"/></svg>
            </a>
            <a href="https://x.com/repvantage" target="_blank" rel="noopener noreferrer" aria-label="X">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.9 2H22l-6.8 7.8L23.2 22h-6.3l-4.9-6.4L6.4 22H3.3l7.3-8.3L1.6 2H8l4.4 5.9L18.9 2zm-1.1 18.1h1.7L7.1 3.8H5.3l12.5 16.3z"/></svg>
            </a>
            <span style={{ marginLeft: 4 }}>&copy; 2026 REPVANTAGE</span>
          </span>
        </div>
      )}
    </main>
  );
}
